import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { apiJson, formatApiErrorForDisplay } from '@/lib/api';
import { track } from '@/lib/analytics';
import { OutreachReplyAttachmentPicker } from '@/components/admin/OutreachReplyAttachmentPicker';
import { sendOutreachReply } from '@/lib/outreachReplySend';
import { CoachEmailDomainHealthCard } from '@/components/admin/CoachEmailDomainHealthCard';
import { AdminUnreadDot } from '@/components/admin/AdminUnreadDot';
import { DraftRowDomainIndicator } from '@/components/admin/DraftRowDomainIndicator';
import { useLawyerOutreachUnreadReplies } from '@/hooks/useLawyerOutreachUnreadReplies';
import { prefetchEmailDomainHealth } from '@/lib/emailDomainHealthCache';
import { LAWYER_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/lawyerOutreachRepliesStorage';

const LAWYER_OUTREACH_QUERY_KEY = ['admin', 'lawyer-outreach-artifact'] as const;
const LAWYER_OUTREACH_CONTROL_QUERY_KEY = [
  'admin',
  'lawyer-outreach-control',
] as const;
const LAWYER_OUTREACH_PROSPECT_CURSORS_QUERY_KEY = [
  'admin',
  'lawyer-outreach-prospect-cursors',
] as const;

function parseDetail(err: unknown): string {
  return formatApiErrorForDisplay(err);
}

const FREEMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'mail.com',
  'yandex.com',
  'gmx.com',
  'zoho.com',
  'msn.com',
  'comcast.net',
  'att.net',
]);

function inferLawyerWebsiteFromEmail(
  email: string | null | undefined
): string | null {
  const normalized = (email ?? '').trim().toLowerCase();
  const at = normalized.indexOf('@');
  if (at < 0) return null;
  const domain = normalized.slice(at + 1);
  if (!domain || FREEMAIL_DOMAINS.has(domain)) return null;
  for (const freemail of FREEMAIL_DOMAINS) {
    if (domain.endsWith(`.${freemail}`)) return null;
  }
  return `https://${domain}`;
}

function lawyerOutreachWebsiteUrl(contact: {
  website?: string | null;
  email?: string | null;
}): string | null {
  const stored = (contact.website ?? '').trim();
  if (stored) return stored;
  return inferLawyerWebsiteFromEmail(contact.email);
}

type ConfirmDialogState = {
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  secondaryAction?: {
    label: string;
    onConfirm: () => void;
    primary?: boolean;
  };
};

type SaveDraftVars = {
  status: 'draft' | 'approved';
  subject: string;
  body: string;
};

type LawyerOutreachArtifactStatusResponse = {
  artifact_path: string;
  artifact_exists: boolean;
  artifact_contacts: number;
  artifact_emails: number;
  db_contacts: number;
  db_contacts_with_email: number;
  db_profile_only_contacts: number;
  db_email_records: number;
  db_approved_email_records: number;
  db_sent_or_legacy_email_records: number;
  db_contacts_contacted_count: number;
  lawyers_joined_count: number;
  lawyers_joined_among_contacted_count: number;
};

type LawyerOutreachPriorTouchRow = {
  email_id: number;
  email_key: string;
  status: string;
  subject: string | null;
  body: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  complained_at: string | null;
  last_event: string | null;
};

type LawyerOutreachDraftRow = {
  contact_id: number;
  lawyer_name: string;
  email: string | null;
  phone: string | null;
  state: string | null;
  website: string | null;
  profile_url: string | null;
  contact_url: string | null;
  source: string | null;
  firm: string | null;
  practice_focus: string | null;
  summary: string | null;
  notes: string | null;
  contact_status: string;
  tier: number | null;
  email_id: number | null;
  email_key: string;
  email_status: string | null;
  subject: string | null;
  body: string | null;
  sent_at: string | null;
  updated_at: string | null;
  prior_touches: LawyerOutreachPriorTouchRow[];
  is_signed_up: boolean;
};

type FilterOption = {
  label: string;
  value: string;
  help: string;
};

type StatusFilterOption = FilterOption & {
  /** When set, the filter is only meaningful for these touches and is hidden otherwise. */
  onlyForTouches?: string[];
};

const STATUS_FILTERS: StatusFilterOption[] = [
  {
    label: 'Needs review',
    value: 'needs_review',
    help: 'Needs review, no draft yet, and saved drafts — everything still in your review queue for this touch.',
  },
  {
    label: 'Due for follow-up',
    value: 'due',
    help: 'Touch_1 sent ≥3d ago for touch_2, ≥10d ago for touch_3, with no row created yet for the selected touch. Contacts with any bounced email are excluded.',
    onlyForTouches: ['touch_2', 'touch_3'],
  },
  {
    label: 'Already sent',
    value: 'legacy_sent',
    help: 'Imported emails previously marked sent from the lawyer outreach artifact.',
  },
  {
    label: 'Sent',
    value: 'sent',
    help: 'Emails sent by the backend through Resend.',
  },
  {
    label: 'Approved',
    value: 'approved',
    help: 'Eligible for Celery once sending is enabled.',
  },
  {
    label: 'Skipped',
    value: 'skipped',
    help: 'Touches a human chose not to send. The Celery sender ignores them.',
  },
  {
    label: 'All',
    value: 'all',
    help: 'All contacts for this touch, regardless of draft status.',
  },
];

const TOUCH_FILTERS: FilterOption[] = [
  {
    label: 'Touch 1',
    value: 'touch_1',
    help: 'First outreach email.',
  },
  {
    label: 'Touch 2',
    value: 'touch_2',
    help: 'Second follow-up touch. Lawyers with a prior bounce are hidden.',
  },
  {
    label: 'Touch 3',
    value: 'touch_3',
    help: 'Final follow-up touch. Lawyers with a prior bounce are hidden.',
  },
];

const PRACTICE_FOCUS_FILTERS: FilterOption[] = [
  {
    label: 'All focuses',
    value: 'all',
    help: 'No practice-focus filter.',
  },
  {
    label: 'Enforcement',
    value: 'enforcement',
    help: 'Contacts tagged practice_focus=enforcement.',
  },
  {
    label: 'Custody',
    value: 'custody',
    help: 'Contacts tagged practice_focus=custody.',
  },
  {
    label: 'General',
    value: 'general',
    help: 'Contacts tagged practice_focus=general.',
  },
];

type LawyerOutreachDraftListResponse = {
  rows: LawyerOutreachDraftRow[];
  total: number;
  limit: number;
  offset: number;
  email_key: string;
};

type LawyerOutreachDraftUpsertResponse = {
  email_id: number;
  contact_id: number;
  email_key: string;
  status: string;
  message: string;
};

type LawyerOutreachDraftSkipResponse = {
  email_id: number;
  contact_id: number;
  email_key: string;
  status: string;
  message: string;
};

type LawyerOutreachContactCreateResponse = {
  contact_id: number;
  lawyer_name: string;
  email: string;
  source: string;
  status: string;
  message: string;
};

type LawyerOutreachProspectRequest = {
  dry_run: boolean;
  sources: string[];
  pages_per_run: number;
  limit: number;
  require_website: boolean;
  auto_resume: boolean;
  reset_cursors: boolean;
  consecutive_empty_stop: number;
};

type LawyerOutreachProspectStartResponse = {
  job_id: string;
  message: string;
};

type LawyerOutreachProspectResultPayload = {
  candidate_leads: number;
  inserted: number;
  matched_or_enriched: number;
  skipped_ineligible: number;
  skipped_existing_professional: number;
  skipped_duplicate: number;
  source_counts: Record<string, number>;
  errors: string[];
  contacts_before: number;
  contacts_after: number;
  dry_run: boolean;
  pages_scanned: Record<string, number>;
  new_per_source: Record<string, number>;
  source_state: Record<string, ProspectSourceCursor>;
};

type ProspectSourceCursor = {
  next_page: number;
  exhausted: boolean;
  last_run_at: string | null;
  last_new_count: number;
};

type LawyerOutreachProspectCursorsResponse = {
  cursors: Record<string, ProspectSourceCursor>;
};

type LawyerOutreachProspectJobResponse = {
  job_id: string;
  status: string;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  options: Record<string, unknown> | null;
  result: LawyerOutreachProspectResultPayload | null;
  error: string | null;
};

const PROSPECT_SOURCE_OPTIONS = [
  { id: 'justia_family', label: 'Justia (family law)' },
  { id: 'avvo_family', label: 'Avvo (family law)' },
] as const;

const DEFAULT_PROSPECT_FORM: LawyerOutreachProspectRequest = {
  dry_run: false,
  sources: PROSPECT_SOURCE_OPTIONS.map((s) => s.id),
  pages_per_run: 3,
  limit: 1000,
  require_website: false,
  auto_resume: true,
  reset_cursors: false,
  consecutive_empty_stop: 2,
};

type LawyerOutreachControlStatusResponse = {
  enabled: boolean;
  enabled_source: 'redis' | 'env_var' | 'default_off' | string;
  paused: boolean;
  redis_configured: boolean;
  daily_limit: number;
  approved_count: number;
  schedule_environment: 'production' | 'dev' | string;
  schedule_description: string;
  next_run_at_utc: string | null;
  next_run_in_seconds: number | null;
};

type LawyerOutreachControlUpdateResponse = {
  enabled: boolean;
  paused: boolean;
  enabled_source: string;
  message: string;
};

type LawyerOutreachMacroRow = {
  id: number;
  name: string;
  description: string | null;
  email_key: string | null;
  subject: string;
  body: string;
  created_at: string;
  updated_at: string;
};

type LawyerOutreachMacroListResponse = {
  macros: LawyerOutreachMacroRow[];
};

type LawyerOutreachMacroSaveResponse = {
  macro: LawyerOutreachMacroRow;
  created: boolean;
  message: string;
};

type LawyerOutreachMacroDeleteResponse = {
  deleted_id: number;
  message: string;
};

type LawyerOutreachReplyContactRef = {
  id: number;
  lawyer_name: string;
  email: string | null;
  status: string;
};

type LawyerOutreachReplyRow = {
  id: string;
  from_email: string;
  to_emails: string[];
  subject: string;
  received_at: string | null;
  message_id: string | null;
  has_attachments: boolean;
  matched_contact: LawyerOutreachReplyContactRef | null;
  manual_reply_sent_at: string | null;
};

type LawyerOutreachRepliesListResponse = {
  replies: LawyerOutreachReplyRow[];
  has_more: boolean;
  fetched_at: string;
  error: string | null;
};

type LawyerOutreachReplyDetailResponse = {
  id: string;
  from_email: string;
  to_emails: string[];
  cc: string[];
  bcc: string[];
  reply_to: string[];
  subject: string;
  received_at: string | null;
  message_id: string | null;
  html: string | null;
  text: string | null;
  attachments: Array<{
    id?: string;
    filename?: string;
    content_type?: string;
    content_disposition?: string;
  }>;
  matched_contact: LawyerOutreachReplyContactRef | null;
  manual_reply_sent_at: string | null;
};

type LawyerOutreachReplySendRequest = {
  subject: string;
  body: string;
  include_quoted_original: boolean;
  files: File[];
};

type LawyerOutreachReplySendResponse = {
  message_id: string;
  from_email: string;
  to_email: string;
  subject: string;
  sent_at: string;
  contact_status_after: string | null;
  persisted_email_id: number | null;
  attachment_filenames?: string[];
};

type LawyerOutreachSendRow = {
  id: string;
  from_email: string;
  to_emails: string[];
  subject: string;
  sent_at: string | null;
  last_event: string | null;
  matched_contact: LawyerOutreachReplyContactRef | null;
};

type LawyerOutreachSendsListResponse = {
  sends: LawyerOutreachSendRow[];
  has_more: boolean;
  fetched_at: string;
  error: string | null;
};

type LawyerOutreachSendDetailResponse = {
  id: string;
  from_email: string;
  to_emails: string[];
  cc: string[];
  bcc: string[];
  reply_to: string[];
  subject: string;
  sent_at: string | null;
  last_event: string | null;
  html: string | null;
  text: string | null;
  matched_contact: LawyerOutreachReplyContactRef | null;
};

const SEND_EVENT_BADGE_CLASSES: Record<string, string> = {
  delivered: 'bg-green-100 text-green-800',
  opened: 'bg-blue-100 text-blue-800',
  clicked: 'bg-emerald-100 text-emerald-900',
  bounced: 'bg-red-100 text-red-800',
  complained: 'bg-red-100 text-red-900',
  failed: 'bg-red-100 text-red-800',
  sent: 'bg-gray-100 text-gray-700',
  queued: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-gray-100 text-gray-700',
};

function sendEventBadgeClass(event: string | null): string {
  if (!event) return 'bg-gray-100 text-gray-600';
  return (
    SEND_EVENT_BADGE_CLASSES[event.toLowerCase()] ?? 'bg-gray-100 text-gray-700'
  );
}

type LawyerOutreachPreviewResponse = {
  contact_id: number;
  lawyer_name: string;
  to_email: string | null;
  from_email: string;
  reply_to: string | null;
  subject: string;
  html: string;
  text: string;
  headers: Record<string, string>;
  unsubscribe_url: string;
};

/** Replace ``{{lawyer_name}}`` / ``{{first_name}}`` / ``{{firm}}`` placeholders
 * with values derived from the contact. Other ``{{...}}`` expressions are left
 * as-is so the human can spot and replace them by hand. */
function applyMacroSubstitutions(
  template: string,
  contact: { lawyer_name: string; firm?: string | null }
): { value: string; substitutions: string[] } {
  const firstName = (contact.lawyer_name || '').split(/\s+/)[0] || '';
  const firm = (contact.firm || '').trim();
  const subs = new Set<string>();
  const value = template
    .replace(/\{\{\s*lawyer_name\s*\}\}/g, () => {
      if (contact.lawyer_name) subs.add('lawyer_name');
      return contact.lawyer_name || '';
    })
    .replace(/\{\{\s*first_name\s*\}\}/g, () => {
      if (firstName) subs.add('first_name');
      return firstName;
    })
    .replace(/\{\{\s*firm\s*\}\}/g, () => {
      if (firm) subs.add('firm');
      return firm;
    });
  return { value, substitutions: [...subs] };
}

function formatRelativeSeconds(totalSeconds: number): string {
  if (totalSeconds <= 0) return 'now';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return `${hours}h ${remMinutes}m`;
  }
  if (minutes >= 1) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

function formatConversionRate(numerator: number, denominator: number): string {
  if (denominator <= 0) return '—';
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

function describeEnabledSource(source: string, enabled: boolean): string {
  if (source === 'redis') {
    return enabled
      ? 'Enabled via Redis admin toggle.'
      : 'Disabled via Redis admin toggle (overrides env var).';
  }
  if (source === 'env_var') {
    return 'Enabled via LAWYER_OUTREACH_ENABLED env var (no Redis override). Toggle below to override in Redis.';
  }
  return 'Disabled — no Redis override and no env var. Click Enable to override in Redis.';
}

type AddTestContactForm = {
  lawyer_name: string;
  email: string;
  state: string;
  tier: string;
  firm: string;
  practice_focus: string;
  profile_url: string;
  website: string;
  notes: string;
};

const EMPTY_TEST_CONTACT_FORM: AddTestContactForm = {
  lawyer_name: '',
  email: '',
  state: '',
  tier: '',
  firm: '',
  practice_focus: '',
  profile_url: '',
  website: '',
  notes: '',
};

const TOUCH_LABELS: Record<string, string> = {
  touch_1: 'Touch 1',
  touch_2: 'Touch 2',
  touch_3: 'Touch 3',
  manual_reply: 'Manual reply',
};

const LAWYER_OUTREACH_FROM_DISPLAY =
  'Jose at Harbor <jose@hello.harborcoparent.com>';

function dedupeReplySubject(subject: string): string {
  const base = (subject ?? '').trim();
  if (!base) return 'Re:';
  if (/^(re|fwd|fw):/i.test(base)) return base;
  return `Re: ${base}`;
}

function formatTimestamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatRelative(value: string | null): string {
  if (!value) return '—';
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return value;
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(value).toLocaleDateString();
}

function summarizePriorEngagement(prior: LawyerOutreachPriorTouchRow): string {
  if (!prior.sent_at) return 'Not sent yet.';
  const events: string[] = [];
  if (prior.complained_at) events.push('complained');
  if (prior.bounced_at) events.push('bounced');
  if (prior.clicked_at) events.push('clicked');
  if (prior.opened_at) events.push('opened');
  if (prior.delivered_at && events.length === 0) events.push('delivered');
  if (events.length === 0) return 'Sent. No engagement recorded yet.';
  return `Sent. ${events.join(' · ')}.`;
}

type LawyerOutreachDraftGenerateResponse = {
  subject: string;
  body: string;
  model: string;
};

function draftEditorIsDirty(
  row: LawyerOutreachDraftRow | null,
  subject: string,
  body: string
): boolean {
  if (!row) return false;
  return (
    subject.trim() !== (row.subject ?? '').trim() ||
    body.trim() !== (row.body ?? '').trim()
  );
}

/** Bare `<>` in copy means an unfilled macro slot — block approval until resolved. */
function lawyerOutreachApprovalCopyError(
  subject: string,
  body: string
): string | null {
  if (subject.includes('<>')) {
    return 'Cannot approve: subject contains "<>". Replace empty placeholder brackets before approving.';
  }
  if (body.includes('<>')) {
    return 'Cannot approve: body contains "<>". Replace empty placeholder brackets before approving.';
  }
  return null;
}

type DraftListAdvance = { index: number; contactId: number };

function lawyerOutreachRowHasEmail(row: LawyerOutreachDraftRow): boolean {
  return Boolean(row.email?.trim());
}

function lawyerOutreachRowIsSkipped(row: LawyerOutreachDraftRow): boolean {
  return row.email_status === 'skipped';
}

function lawyerOutreachRowIsSignedUp(row: LawyerOutreachDraftRow): boolean {
  return row.is_signed_up;
}

function isFollowUpOutreachTouch(emailKey: string): boolean {
  return emailKey === 'touch_2' || emailKey === 'touch_3';
}

type FilterDraftListOptions = {
  hideSkipped?: boolean;
  hideSignedUp?: boolean;
  practiceFocus?: string;
};

function filterDraftListRows(
  rows: LawyerOutreachDraftRow[],
  options?: FilterDraftListOptions
): LawyerOutreachDraftRow[] {
  return rows.filter((row) => {
    if (!lawyerOutreachRowHasEmail(row)) return false;
    if (options?.hideSkipped && lawyerOutreachRowIsSkipped(row)) return false;
    if (options?.hideSignedUp && lawyerOutreachRowIsSignedUp(row)) return false;
    if (
      options?.practiceFocus &&
      options.practiceFocus !== 'all' &&
      (row.practice_focus || '').toLowerCase() !== options.practiceFocus
    ) {
      return false;
    }
    return true;
  });
}

function captureDraftListAdvance(
  rows: LawyerOutreachDraftRow[],
  contactId: number
): DraftListAdvance | null {
  const index = rows.findIndex((row) => row.contact_id === contactId);
  return index >= 0 ? { index, contactId } : null;
}

/** After approve/skip, pick the next row in the current list (or slide up if removed). */
function selectNextDraftRow(
  rows: LawyerOutreachDraftRow[],
  previousContactId: number,
  previousIndex: number
): LawyerOutreachDraftRow | null {
  if (rows.length === 0) return null;
  if (rows[previousIndex]?.contact_id === previousContactId) {
    if (previousIndex + 1 < rows.length) return rows[previousIndex + 1] ?? null;
    if (previousIndex > 0) return rows[previousIndex - 1] ?? null;
    return null;
  }
  return rows[Math.min(previousIndex, rows.length - 1)] ?? null;
}

function ConfirmDialog({
  open,
  state,
  onClose,
}: {
  open: boolean;
  state: ConfirmDialogState | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !state) return null;

  const confirmClass = state.destructive
    ? 'bg-red-600 text-white hover:bg-red-700'
    : 'bg-primary text-white hover:opacity-90';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div
        className='absolute inset-0 bg-black/40'
        aria-hidden
        onClick={onClose}
      />
      <div
        className='relative z-10 bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 border border-gray-200'
        role='dialog'
        aria-modal='true'
        aria-labelledby='lawyer-confirm-dialog-title'
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id='lawyer-confirm-dialog-title'
          className='text-lg font-semibold text-gray-900'
        >
          {state.title}
        </h3>
        <p className='text-sm text-gray-600 whitespace-pre-wrap'>
          {state.message}
        </p>
        <div className='flex justify-end gap-2 pt-2 flex-wrap'>
          <button
            type='button'
            onClick={onClose}
            className='px-4 py-2 text-sm font-semibold border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50'
          >
            Cancel
          </button>
          {state.secondaryAction ? (
            <button
              type='button'
              onClick={() => {
                state.secondaryAction?.onConfirm();
                onClose();
              }}
              className={`px-4 py-2 text-sm font-semibold rounded-md ${
                state.secondaryAction.primary
                  ? 'bg-primary text-white hover:opacity-90'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {state.secondaryAction.label}
            </button>
          ) : null}
          <button
            type='button'
            onClick={() => {
              state.onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-sm font-semibold rounded-md ${
              state.secondaryAction?.primary
                ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                : confirmClass
            }`}
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function LawyerOutreachAdminPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(
    null
  );
  const [draftEmailKey, setDraftEmailKey] = useState('touch_1');
  const [draftStatus, setDraftStatus] = useState('needs_review');
  const [draftPracticeFocus, setDraftPracticeFocus] = useState('all');
  const [showSkippedLawyers, setShowSkippedLawyers] = useState(false);
  const [showSignedUpLawyers, setShowSignedUpLawyers] = useState(false);
  const [draftSearch, setDraftSearch] = useState('');
  const [selectedDraft, setSelectedDraft] =
    useState<LawyerOutreachDraftRow | null>(null);
  const pendingDraftAdvanceRef = useRef<DraftListAdvance | null>(null);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [generateInstructions, setGenerateInstructions] = useState('');
  const [draftBanner, setDraftBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [testContactForm, setTestContactForm] = useState<AddTestContactForm>(
    EMPTY_TEST_CONTACT_FORM
  );
  const [testContactBanner, setTestContactBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [prospectForm, setProspectForm] =
    useState<LawyerOutreachProspectRequest>(DEFAULT_PROSPECT_FORM);
  const [prospectJobId, setProspectJobId] = useState<string | null>(null);
  const [prospectJobStatus, setProspectJobStatus] =
    useState<LawyerOutreachProspectJobResponse | null>(null);
  const [prospectBanner, setProspectBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [csvDryRun, setCsvDryRun] = useState(true);
  const [csvBanner, setCsvBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const prospectPanelViewedRef = useRef(false);

  const lawyerOutreachQuery = useQuery({
    queryKey: LAWYER_OUTREACH_QUERY_KEY,
    queryFn: () =>
      apiJson<LawyerOutreachArtifactStatusResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/artifact'
      ),
  });

  // Refetch the control status periodically so the "next run" countdown stays
  // close to real. 30s is a sweet spot: short enough to feel live, long enough
  // not to thrash Redis on a page that's left open in a tab.
  const controlQuery = useQuery({
    queryKey: LAWYER_OUTREACH_CONTROL_QUERY_KEY,
    queryFn: () =>
      apiJson<LawyerOutreachControlStatusResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/control'
      ),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const prospectCursorsQuery = useQuery({
    queryKey: LAWYER_OUTREACH_PROSPECT_CURSORS_QUERY_KEY,
    queryFn: () =>
      apiJson<LawyerOutreachProspectCursorsResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/prospect/cursors'
      ),
    staleTime: 30_000,
  });

  const [controlBanner, setControlBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  // Local-only seconds-until-next-run countdown so the UI ticks every second
  // between the 30s server refetches.
  const serverNextRunSeconds = controlQuery.data?.next_run_in_seconds ?? null;
  const [secondsUntilNextRun, setSecondsUntilNextRun] = useState<number | null>(
    serverNextRunSeconds
  );
  useEffect(() => {
    setSecondsUntilNextRun(serverNextRunSeconds);
  }, [serverNextRunSeconds]);
  useEffect(() => {
    if (serverNextRunSeconds === null) return;
    const id = window.setInterval(() => {
      setSecondsUntilNextRun((prev) =>
        prev === null ? null : Math.max(0, prev - 1)
      );
    }, 1000);
    return () => window.clearInterval(id);
  }, [serverNextRunSeconds]);

  const repliesQuery = useQuery({
    queryKey: LAWYER_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<LawyerOutreachRepliesListResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/replies?limit=50'
      ),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadReplies, markRepliesOpened } =
    useLawyerOutreachUnreadReplies(repliesQuery.data?.replies);

  const [openReplyId, setOpenReplyId] = useState<string | null>(null);
  const [openReplyMode, setOpenReplyMode] = useState<'html' | 'text'>('html');
  const replyDetailQuery = useQuery({
    queryKey: ['admin', 'lawyer-outreach-reply', openReplyId] as const,
    queryFn: () =>
      apiJson<LawyerOutreachReplyDetailResponse>(
        getToken,
        `/v1/admin/lawyer-outreach/replies/${encodeURIComponent(
          openReplyId ?? ''
        )}`
      ),
    enabled: openReplyId !== null,
  });

  const sendReplyMutation = useMutation({
    mutationFn: (vars: {
      replyId: string;
      payload: LawyerOutreachReplySendRequest;
    }) =>
      sendOutreachReply<LawyerOutreachReplySendResponse>(
        getToken,
        `/v1/admin/lawyer-outreach/replies/${encodeURIComponent(vars.replyId)}/send`,
        vars.payload,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: LAWYER_OUTREACH_REPLIES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'lawyer-outreach-reply', vars.replyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'lawyer-outreach-sends'],
      });
      queryClient.invalidateQueries({ queryKey: LAWYER_OUTREACH_QUERY_KEY });
    },
  });

  const sendsQuery = useQuery({
    queryKey: ['admin', 'lawyer-outreach-sends'] as const,
    queryFn: () =>
      apiJson<LawyerOutreachSendsListResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/sends?limit=50'
      ),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const [openSendId, setOpenSendId] = useState<string | null>(null);
  const [openSendMode, setOpenSendMode] = useState<'html' | 'text'>('html');
  const sendDetailQuery = useQuery({
    queryKey: ['admin', 'lawyer-outreach-send', openSendId] as const,
    queryFn: () =>
      apiJson<LawyerOutreachSendDetailResponse>(
        getToken,
        `/v1/admin/lawyer-outreach/sends/${encodeURIComponent(openSendId ?? '')}`
      ),
    enabled: openSendId !== null,
  });

  const controlMutation = useMutation({
    mutationFn: (patch: { enabled?: boolean; paused?: boolean }) =>
      apiJson<LawyerOutreachControlUpdateResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/control',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        }
      ),
    onSuccess: async (data) => {
      setControlBanner({ ok: true, text: data.message });
      await controlQuery.refetch();
    },
    onError: (e) => setControlBanner({ ok: false, text: parseDetail(e) }),
  });

  const draftParams = new URLSearchParams({
    email_key: draftEmailKey,
    status: draftStatus,
    limit: '50',
    offset: '0',
  });
  if (draftSearch.trim()) draftParams.set('q', draftSearch.trim());

  const draftsQuery = useQuery({
    queryKey: ['admin', 'lawyer-outreach-drafts', draftParams.toString()],
    queryFn: () =>
      apiJson<LawyerOutreachDraftListResponse>(
        getToken,
        `/v1/admin/lawyer-outreach/drafts?${draftParams.toString()}`
      ),
  });

  const saveDraftMutation = useMutation({
    mutationFn: (vars: SaveDraftVars) => {
      if (!selectedDraft) throw new Error('Select a contact first');
      return apiJson<LawyerOutreachDraftUpsertResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/drafts',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: selectedDraft.contact_id,
            email_key: draftEmailKey,
            subject: vars.subject.trim(),
            body: vars.body.trim(),
            status: vars.status,
          }),
        }
      );
    },
    onMutate: (vars) => {
      if (vars.status !== 'approved' || !selectedDraft) return;
      pendingDraftAdvanceRef.current = captureDraftListAdvance(
        filterDraftListRows(draftsQuery.data?.rows ?? [], draftListFilterOptions),
        selectedDraft.contact_id
      );
    },
    onSuccess: async (data, vars) => {
      setDraftBanner({ ok: true, text: data.message });
      const approvedContactId = selectedDraft?.contact_id;
      const result = await draftsQuery.refetch();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: LAWYER_OUTREACH_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: LAWYER_OUTREACH_CONTROL_QUERY_KEY,
        }),
      ]);

      const rows = filterDraftListRows(
        result.data?.rows ?? [],
        draftListFilterOptions
      );

      if (vars.status === 'approved') {
        const advance = pendingDraftAdvanceRef.current;
        pendingDraftAdvanceRef.current = null;
        const nextRow =
          advance !== null
            ? selectNextDraftRow(rows, advance.contactId, advance.index)
            : null;
        if (nextRow) {
          handleReviewDraft(nextRow);
        } else {
          setSelectedDraft(null);
        }
      } else if (approvedContactId != null) {
        const updated = rows.find(
          (row) => row.contact_id === approvedContactId
        );
        if (updated) setSelectedDraft(updated);
      }
    },
    onError: (e) => {
      pendingDraftAdvanceRef.current = null;
      setDraftBanner({ ok: false, text: parseDetail(e) });
    },
  });

  const generateDraftMutation = useMutation({
    mutationFn: () => {
      if (!selectedDraft) throw new Error('Select a contact first');
      return apiJson<LawyerOutreachDraftGenerateResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/generate-draft',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: selectedDraft.contact_id,
            email_key: draftEmailKey,
            instructions: generateInstructions.trim() || null,
          }),
        }
      );
    },
    onSuccess: (data) => {
      setDraftSubject(data.subject);
      setDraftBody(data.body);
      setDraftBanner({
        ok: true,
        text: `Generated draft with ${data.model}. Review and save or approve when ready.`,
      });
    },
    onError: (e) => setDraftBanner({ ok: false, text: parseDetail(e) }),
  });

  const createTestContactMutation = useMutation({
    mutationFn: (form: AddTestContactForm) => {
      const tier = form.tier.trim();
      const payload: Record<string, unknown> = {
        lawyer_name: form.lawyer_name.trim(),
        email: form.email.trim(),
      };
      if (form.state.trim()) payload.state = form.state.trim();
      if (tier) payload.tier = Number(tier);
      if (form.firm.trim()) payload.firm = form.firm.trim();
      if (form.practice_focus.trim())
        payload.practice_focus = form.practice_focus.trim();
      if (form.profile_url.trim()) payload.profile_url = form.profile_url.trim();
      if (form.website.trim()) payload.website = form.website.trim();
      if (form.notes.trim()) payload.notes = form.notes.trim();
      return apiJson<LawyerOutreachContactCreateResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/contacts',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
    },
    onSuccess: async (data) => {
      setTestContactBanner({ ok: true, text: data.message });
      setTestContactForm(EMPTY_TEST_CONTACT_FORM);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: LAWYER_OUTREACH_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: LAWYER_OUTREACH_CONTROL_QUERY_KEY,
        }),
      ]);
      await draftsQuery.refetch();
    },
    onError: (e) =>
      setTestContactBanner({ ok: false, text: parseDetail(e) }),
  });

  const startProspectMutation = useMutation({
    mutationFn: (payload: LawyerOutreachProspectRequest) =>
      apiJson<LawyerOutreachProspectStartResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/prospect',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: (data) => {
      setProspectJobId(data.job_id);
      setProspectJobStatus(null);
      setProspectBanner({
        ok: true,
        text: 'Prospecting job queued. This may take several minutes…',
      });
    },
    onError: (e) =>
      setProspectBanner({ ok: false, text: parseDetail(e) }),
  });

  const importCsvMutation = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return apiJson<{
        candidate_leads: number;
        inserted: number;
        matched_or_enriched: number;
        skipped_ineligible: number;
        skipped_existing_professional: number;
        dry_run: boolean;
        message: string;
      }>(
        getToken,
        `/v1/admin/lawyer-outreach/import-csv?dry_run=${csvDryRun ? 'true' : 'false'}`,
        {
          method: 'POST',
          body: form,
        }
      );
    },
    onSuccess: async (data) => {
      setCsvBanner({
        ok: true,
        text: `${data.message} Candidates=${data.candidate_leads}, inserted=${data.inserted}, enriched=${data.matched_or_enriched}.`,
      });
      track('Lawyer Outreach CSV Imported', {
        dry_run: data.dry_run,
        inserted: data.inserted,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: LAWYER_OUTREACH_QUERY_KEY }),
        queryClient.invalidateQueries({
          queryKey: ['admin', 'lawyer-outreach-drafts'],
        }),
      ]);
      await draftsQuery.refetch();
    },
    onError: (e) => setCsvBanner({ ok: false, text: parseDetail(e) }),
  });

  useEffect(() => {
    if (!prospectJobId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const status = await apiJson<LawyerOutreachProspectJobResponse>(
          getToken,
          `/v1/admin/lawyer-outreach/prospect/${encodeURIComponent(prospectJobId)}`
        );
        if (cancelled) return;
        setProspectJobStatus(status);

        if (status.status === 'completed') {
          const r = status.result;
          const summary = r
            ? `Done${r.dry_run ? ' (dry run)' : ''}: ${r.inserted} inserted, ${r.matched_or_enriched} enriched, ${r.skipped_ineligible} ineligible, ${r.skipped_existing_professional} existing professionals.`
            : 'Prospecting completed.';
          setProspectBanner({ ok: true, text: summary });
          setProspectJobId(null);
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: LAWYER_OUTREACH_QUERY_KEY,
            }),
            queryClient.invalidateQueries({
              queryKey: ['admin', 'lawyer-outreach-drafts'],
            }),
            queryClient.invalidateQueries({
              queryKey: LAWYER_OUTREACH_PROSPECT_CURSORS_QUERY_KEY,
            }),
          ]);
          await draftsQuery.refetch();
          return;
        }
        if (status.status === 'failed') {
          setProspectBanner({
            ok: false,
            text: status.error ?? 'Prospecting job failed.',
          });
          setProspectJobId(null);
          return;
        }
      } catch (e) {
        if (!cancelled) {
          setProspectBanner({ ok: false, text: parseDetail(e) });
          setProspectJobId(null);
        }
        return;
      }

      if (!cancelled) {
        window.setTimeout(poll, 2500);
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
  }, [prospectJobId, getToken, queryClient]);

  const macrosQueryKey = ['admin', 'lawyer-outreach-macros'] as const;
  const macrosQuery = useQuery({
    queryKey: macrosQueryKey,
    queryFn: () =>
      apiJson<LawyerOutreachMacroListResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/macros'
      ),
  });

  const [macroSaveName, setMacroSaveName] = useState('');
  const [macroSaveDescription, setMacroSaveDescription] = useState('');
  const [macroSaveScopeToTouch, setMacroSaveScopeToTouch] = useState(false);
  const [macroBanner, setMacroBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const saveMacroMutation = useMutation({
    mutationFn: (payload: {
      name: string;
      description: string | null;
      email_key: string | null;
      subject: string;
      body: string;
    }) =>
      apiJson<LawyerOutreachMacroSaveResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/macros',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: async (data) => {
      setMacroBanner({ ok: true, text: data.message });
      setMacroSaveName('');
      setMacroSaveDescription('');
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'lawyer-outreach-macros'],
      });
    },
    onError: (e) => setMacroBanner({ ok: false, text: parseDetail(e) }),
  });

  const deleteMacroMutation = useMutation({
    mutationFn: (macroId: number) =>
      apiJson<LawyerOutreachMacroDeleteResponse>(
        getToken,
        `/v1/admin/lawyer-outreach/macros/${macroId}`,
        { method: 'DELETE' }
      ),
    onSuccess: async (data) => {
      setMacroBanner({ ok: true, text: data.message });
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'lawyer-outreach-macros'],
      });
    },
    onError: (e) => setMacroBanner({ ok: false, text: parseDetail(e) }),
  });

  const handleLoadMacro = (macro: LawyerOutreachMacroRow) => {
    if (!selectedDraft) return;
    const subjectResult = applyMacroSubstitutions(macro.subject, selectedDraft);
    const bodyResult = applyMacroSubstitutions(macro.body, selectedDraft);
    setDraftSubject(subjectResult.value);
    setDraftBody(bodyResult.value);
    const subs = [
      ...new Set([
        ...subjectResult.substitutions,
        ...bodyResult.substitutions,
      ]),
    ];
    const quotedName = JSON.stringify(macro.name);
    setMacroBanner({
      ok: true,
      text:
        subs.length > 0
          ? `Loaded macro ${quotedName}. Substituted: ${subs.join(', ')}.`
          : `Loaded macro ${quotedName}. No substitutions.`,
    });
  };

  const [previewResult, setPreviewResult] =
    useState<LawyerOutreachPreviewResponse | null>(null);
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');
  const [previewError, setPreviewError] = useState<string | null>(null);

  const previewDraftMutation = useMutation({
    mutationFn: (payload: {
      contact_id: number;
      subject: string;
      body: string;
    }) =>
      apiJson<LawyerOutreachPreviewResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/preview',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: (data) => {
      setPreviewError(null);
      setPreviewMode('html');
      setPreviewResult(data);
    },
    onError: (e) => setPreviewError(parseDetail(e)),
  });

  const skipDraftMutation = useMutation({
    mutationFn: () => {
      if (!selectedDraft) throw new Error('Select a contact first');
      return apiJson<LawyerOutreachDraftSkipResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/skip',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: selectedDraft.contact_id,
            email_key: draftEmailKey,
          }),
        }
      );
    },
    onMutate: () => {
      if (!selectedDraft) return;
      pendingDraftAdvanceRef.current = captureDraftListAdvance(
        filterDraftListRows(draftsQuery.data?.rows ?? [], draftListFilterOptions),
        selectedDraft.contact_id
      );
    },
    onSuccess: async (data) => {
      setDraftBanner({ ok: true, text: data.message });
      const result = await draftsQuery.refetch();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: LAWYER_OUTREACH_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: LAWYER_OUTREACH_CONTROL_QUERY_KEY,
        }),
      ]);

      const advance = pendingDraftAdvanceRef.current;
      pendingDraftAdvanceRef.current = null;
      const rows = filterDraftListRows(
        result.data?.rows ?? [],
        draftListFilterOptions
      );
      const nextRow =
        advance !== null
          ? selectNextDraftRow(rows, advance.contactId, advance.index)
          : null;
      if (nextRow) {
        handleReviewDraft(nextRow);
      } else {
        setSelectedDraft(null);
      }
    },
    onError: (e) => {
      pendingDraftAdvanceRef.current = null;
      setDraftBanner({ ok: false, text: parseDetail(e) });
    },
  });

  const handleReviewDraft = (row: LawyerOutreachDraftRow) => {
    setSelectedDraft(row);
  };

  const hideSkippedInDraftList =
    isFollowUpOutreachTouch(draftEmailKey) &&
    !showSkippedLawyers &&
    draftStatus !== 'skipped';
  const hideSignedUpInDraftList =
    isFollowUpOutreachTouch(draftEmailKey) &&
    !showSignedUpLawyers &&
    draftStatus !== 'skipped';
  const draftListFilterOptions: FilterDraftListOptions = {
    hideSkipped: hideSkippedInDraftList,
    hideSignedUp: hideSignedUpInDraftList,
    practiceFocus: draftPracticeFocus,
  };

  const lawyerOutreachStatus = lawyerOutreachQuery.data;
  const visibleStatusFilters = STATUS_FILTERS.filter(
    (filter) =>
      !filter.onlyForTouches || filter.onlyForTouches.includes(draftEmailKey)
  );
  const selectedStatusFilter =
    visibleStatusFilters.find((filter) => filter.value === draftStatus) ?? null;
  const selectedTouchFilter =
    TOUCH_FILTERS.find((filter) => filter.value === draftEmailKey) ?? null;
  const selectedPracticeFocusFilter =
    PRACTICE_FOCUS_FILTERS.find((filter) => filter.value === draftPracticeFocus) ??
    null;
  const selectedDraftLocked =
    selectedDraft?.sent_at !== null ||
    selectedDraft?.email_status === 'legacy_sent';
  const selectedDraftAlreadySkipped =
    selectedDraft?.email_status === 'skipped';
  const selectedDraftSentLabel =
    selectedDraft?.email_status === 'legacy_sent'
      ? 'Imported legacy send'
      : selectedDraft?.email_status === 'sent'
      ? 'Sent via Resend'
      : null;
  const priorTouches = selectedDraft?.prior_touches ?? [];
  const draftIsDirty = draftEditorIsDirty(
    selectedDraft,
    draftSubject,
    draftBody
  );
  const draftListRows = filterDraftListRows(
    draftsQuery.data?.rows ?? [],
    draftListFilterOptions
  );

  useEffect(() => {
    const rows = draftsQuery.data?.rows;
    if (!rows?.length) return;
    prefetchEmailDomainHealth(
      filterDraftListRows(rows, draftListFilterOptions).map((row) => row.email)
    );
  }, [draftsQuery.data, hideSkippedInDraftList, hideSignedUpInDraftList, draftPracticeFocus]);

  const draftListDisplayedCount = draftListRows.length;
  const draftListTotalCount = draftsQuery.data?.total ?? 0;
  const draftListHasMore =
    draftListTotalCount > draftListDisplayedCount && draftListDisplayedCount > 0;

  const openApproveConfirmDialog = () => {
    const baseMessage =
      'Approved drafts become eligible for the Celery sender once it is enabled (see Sender control above). This click does not send immediately.';
    const approveVars = (subject: string, body: string): SaveDraftVars => ({
      status: 'approved',
      subject,
      body,
    });

    const approveIfValid = (subject: string, body: string) => {
      const approvalError = lawyerOutreachApprovalCopyError(subject, body);
      if (approvalError) {
        setDraftBanner({ ok: false, text: approvalError });
        return;
      }
      saveDraftMutation.mutate(approveVars(subject, body));
    };

    if (draftIsDirty && selectedDraft) {
      setConfirmDialog({
        title: 'Approve lawyer outreach draft?',
        message: `${baseMessage}\n\nYou have unsaved edits in the editor. Approve saved copy uses the last saved subject and body. Save & Approve writes your current text and approves it.`,
        confirmLabel: 'Save & Approve',
        onConfirm: () => approveIfValid(draftSubject, draftBody),
        secondaryAction: {
          label: 'Approve saved copy',
          onConfirm: () =>
            approveIfValid(
              selectedDraft.subject ?? '',
              selectedDraft.body ?? ''
            ),
        },
      });
      return;
    }

    const approvalError = lawyerOutreachApprovalCopyError(
      draftSubject,
      draftBody
    );
    if (approvalError) {
      setDraftBanner({ ok: false, text: approvalError });
      return;
    }

    setConfirmDialog({
      title: 'Approve lawyer outreach draft?',
      message: baseMessage,
      confirmLabel: 'Approve draft',
      onConfirm: () => approveIfValid(draftSubject, draftBody),
    });
  };

  useEffect(() => {
    if (!selectedDraft) return;
    setDraftSubject(selectedDraft.subject ?? '');
    setDraftBody(selectedDraft.body ?? '');
    setGenerateInstructions('');
    setDraftBanner(null);
  }, [selectedDraft]);

  // If the user switches the touch filter to one where the current status
  // filter no longer applies (e.g. picks Touch 1 while "Due for follow-up" is
  // active), reset to the safe default. This avoids sending a 422 to the
  // backend and keeps the UI internally consistent.
  useEffect(() => {
    if (draftStatus === 'none' || draftStatus === 'draft') {
      setDraftStatus('needs_review');
      setSelectedDraft(null);
      return;
    }
    const stillVisible = STATUS_FILTERS.find(
      (filter) =>
        filter.value === draftStatus &&
        (!filter.onlyForTouches ||
          filter.onlyForTouches.includes(draftEmailKey))
    );
    if (!stillVisible) {
      setDraftStatus('needs_review');
      setSelectedDraft(null);
    }
    if (!isFollowUpOutreachTouch(draftEmailKey)) {
      setShowSkippedLawyers(false);
      setShowSignedUpLawyers(false);
    }
  }, [draftEmailKey, draftStatus]);

  useEffect(() => {
    if (
      hideSkippedInDraftList &&
      selectedDraft &&
      lawyerOutreachRowIsSkipped(selectedDraft)
    ) {
      setSelectedDraft(null);
    }
  }, [hideSkippedInDraftList, selectedDraft?.contact_id, selectedDraft?.email_status]);

  useEffect(() => {
    if (
      hideSignedUpInDraftList &&
      selectedDraft &&
      lawyerOutreachRowIsSignedUp(selectedDraft)
    ) {
      setSelectedDraft(null);
    }
  }, [
    hideSignedUpInDraftList,
    selectedDraft?.contact_id,
    selectedDraft?.is_signed_up,
  ]);

  useEffect(() => {
    if (selectedDraft && !lawyerOutreachRowHasEmail(selectedDraft)) {
      setSelectedDraft(null);
    }
  }, [selectedDraft?.contact_id, selectedDraft?.email]);

  const control = controlQuery.data;
  const sendingLive = !!control && control.enabled && !control.paused;
  const enabledStatusBadge = control
    ? control.enabled
      ? control.paused
        ? {
            label: 'Enabled, paused',
            className: 'bg-amber-100 text-amber-900 border-amber-200',
          }
        : {
            label: 'Enabled',
            className: 'bg-green-100 text-green-900 border-green-200',
          }
      : {
          label: 'Disabled',
          className: 'bg-red-100 text-red-900 border-red-200',
        }
    : null;

  return (
    <div className='space-y-6'>
      <h1 className='text-2xl font-semibold text-gray-900'>Lawyer Outreach</h1>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>
              Lawyer conversions
            </h2>
            <p className='mt-1 text-sm text-gray-600'>
              Outreach contacts whose email matches a professional user account
              (email-only sign-up, no phone on file).
            </p>
          </div>
          <button
            type='button'
            onClick={() => lawyerOutreachQuery.refetch()}
            className='px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50'
          >
            Refresh
          </button>
        </div>

        {lawyerOutreachQuery.isLoading ? (
          <p className='text-sm text-gray-500'>Loading conversion metrics…</p>
        ) : lawyerOutreachQuery.isError ? (
          <p className='text-sm text-red-600'>
            {parseDetail(lawyerOutreachQuery.error)}
          </p>
        ) : lawyerOutreachStatus ? (
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3'>
            <div className='rounded-lg border border-emerald-200 bg-emerald-50 p-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-emerald-700'>
                Lawyers joined
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-emerald-950'>
                {lawyerOutreachStatus.lawyers_joined_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-emerald-800'>
                {formatConversionRate(
                  lawyerOutreachStatus.lawyers_joined_count,
                  lawyerOutreachStatus.db_contacts_with_email
                )}{' '}
                of {lawyerOutreachStatus.db_contacts_with_email.toLocaleString()}{' '}
                contacts with email
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Joined after outreach
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                {lawyerOutreachStatus.lawyers_joined_among_contacted_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {formatConversionRate(
                  lawyerOutreachStatus.lawyers_joined_among_contacted_count,
                  lawyerOutreachStatus.db_contacts_contacted_count
                )}{' '}
                of {lawyerOutreachStatus.db_contacts_contacted_count.toLocaleString()}{' '}
                contacted lawyeres
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Contacted lawyeres
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                {lawyerOutreachStatus.db_contacts_contacted_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {lawyerOutreachStatus.db_sent_or_legacy_email_records.toLocaleString()}{' '}
                sent or legacy-sent email rows
              </p>
            </div>
          </div>
        ) : (
          <p className='text-sm text-gray-500'>
            No conversion metrics available.
          </p>
        )}
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
        <details
          className='group'
          onToggle={(e) => {
            if (e.currentTarget.open && !prospectPanelViewedRef.current) {
              prospectPanelViewedRef.current = true;
              track('Lawyer Outreach Prospect Screen');
            }
          }}
        >
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>
                Prospect more leads
              </h2>
              <p className='mt-1 text-sm text-gray-600'>
                Import a CSV or discover lawyers from public directories.
                New rows appear under Touch 1 → Needs review with no draft yet.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-gray-500 group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-gray-500 hidden group-open:inline'>
              − Collapse
            </span>
          </summary>

          <div className='mt-4 space-y-4'>
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3'>
              <h3 className='text-sm font-semibold text-gray-900'>
                Import CSV (primary)
              </h3>
              <p className='text-xs text-gray-600'>
                Headers: lawyer_name (or name), email, firm, state, website,
                profile_url, practice_focus (enforcement|custody|general).
              </p>
              <label className='inline-flex items-center gap-2 text-sm text-gray-700'>
                <input
                  type='checkbox'
                  checked={csvDryRun}
                  onChange={(e) => setCsvDryRun(e.target.checked)}
                />
                Dry run (no writes)
              </label>
              <input
                type='file'
                accept='.csv,text/csv'
                className='block text-sm'
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) importCsvMutation.mutate(file);
                  e.currentTarget.value = '';
                }}
              />
              {csvBanner ? (
                <div
                  className={`rounded-lg border px-3 py-2 text-sm ${
                    csvBanner.ok
                      ? 'bg-green-50 border-green-200 text-green-900'
                      : 'bg-red-50 border-red-200 text-red-800'
                  }`}
                >
                  {csvBanner.text}
                </div>
              ) : null}
            </div>

            <p className='text-xs text-amber-700'>
              Directory scrape requires Redis and a running Celery worker.
              Inserts need email, name, and a website or directory profile URL.
              Skips emails that already match a professional Harbor account.
            </p>

            {prospectBanner ? (
              <div
                className={`rounded-lg border px-4 py-3 text-sm ${
                  prospectBanner.ok
                    ? 'bg-green-50 border-green-200 text-green-900'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                {prospectBanner.text}
              </div>
            ) : null}

            {prospectJobId || prospectJobStatus ? (
              <div className='rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'>
                {prospectJobId ? (
                  <p>
                    Job{' '}
                    <code className='text-xs bg-blue-100 px-1 rounded'>
                      {prospectJobId}
                    </code>{' '}
                    — {prospectJobStatus?.status ?? 'polling…'}
                  </p>
                ) : null}
                {prospectJobStatus?.result ? (
                  <ul className='mt-2 text-xs space-y-0.5'>
                    <li>
                      Candidates:{' '}
                      {prospectJobStatus.result.candidate_leads.toLocaleString()}
                    </li>
                    <li>
                      Inserted:{' '}
                      {prospectJobStatus.result.inserted.toLocaleString()}
                    </li>
                    <li>
                      Enriched existing:{' '}
                      {prospectJobStatus.result.matched_or_enriched.toLocaleString()}
                    </li>
                    <li>
                      Skipped (ineligible):{' '}
                      {prospectJobStatus.result.skipped_ineligible.toLocaleString()}
                    </li>
                    <li>
                      Skipped (Harbor pro):{' '}
                      {prospectJobStatus.result.skipped_existing_professional.toLocaleString()}
                    </li>
                    {Object.keys(prospectJobStatus.result.new_per_source ?? {})
                      .length > 0 ? (
                      <li>
                        DB-new this run:{' '}
                        {Object.values(
                          prospectJobStatus.result.new_per_source
                        )
                          .reduce((a, b) => a + b, 0)
                          .toLocaleString()}
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </div>
            ) : null}

            <fieldset className='space-y-2'>
              <legend className='text-xs font-semibold text-gray-600'>
                Directory sources
              </legend>
              <div className='flex flex-col gap-1.5'>
                {PROSPECT_SOURCE_OPTIONS.map((source) => {
                  const checked = prospectForm.sources.includes(source.id);
                  return (
                    <label
                      key={source.id}
                      className='inline-flex items-center gap-2 text-sm text-gray-700'
                    >
                      <input
                        type='checkbox'
                        checked={checked}
                        onChange={(e) => {
                          setProspectForm((prev) => {
                            const next = new Set(prev.sources);
                            if (e.target.checked) next.add(source.id);
                            else next.delete(source.id);
                            return {
                              ...prev,
                              sources: [...next],
                            };
                          });
                        }}
                      />
                      {source.label}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {prospectCursorsQuery.data?.cursors &&
            Object.keys(prospectCursorsQuery.data.cursors).length > 0 ? (
              <div className='rounded-md border border-gray-200 bg-gray-50 px-3 py-2'>
                <p className='text-xs font-semibold text-gray-700 mb-2'>
                  Source cursors (resume state)
                </p>
                <ul className='text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto'>
                  {Object.entries(prospectCursorsQuery.data.cursors)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([source, cursor]) => (
                      <li key={source}>
                        <code className='text-[10px] bg-gray-100 px-1 rounded'>
                          {source}
                        </code>
                        {' — '}
                        page {cursor.next_page}
                        {cursor.exhausted ? ', exhausted' : ''}
                        {cursor.last_new_count != null
                          ? `, last new: ${cursor.last_new_count}`
                          : ''}
                      </li>
                    ))}
                </ul>
              </div>
            ) : prospectCursorsQuery.isError ? (
              <p className='text-xs text-gray-500'>
                Cursor state unavailable (Redis required).
              </p>
            ) : null}

            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
              <div>
                <label className='block text-xs font-semibold text-gray-600 mb-1'>
                  Pages per run
                </label>
                <input
                  type='number'
                  min={1}
                  max={20}
                  value={prospectForm.pages_per_run}
                  onChange={(e) =>
                    setProspectForm((prev) => ({
                      ...prev,
                      pages_per_run: Number(e.target.value) || 1,
                    }))
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
                />
              </div>
              <div>
                <label className='block text-xs font-semibold text-gray-600 mb-1'>
                  Global lead limit
                </label>
                <input
                  type='number'
                  min={1}
                  max={5000}
                  value={prospectForm.limit}
                  onChange={(e) =>
                    setProspectForm((prev) => ({
                      ...prev,
                      limit: Number(e.target.value) || 1,
                    }))
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
                />
              </div>
            </div>

            <div className='flex flex-wrap gap-4 text-sm'>
              <label className='inline-flex items-center gap-2'>
                <input
                  type='checkbox'
                  checked={prospectForm.require_website}
                  onChange={(e) =>
                    setProspectForm((prev) => ({
                      ...prev,
                      require_website: e.target.checked,
                    }))
                  }
                />
                Require own website (not just directory profile)
              </label>
              <label className='inline-flex items-center gap-2'>
                <input
                  type='checkbox'
                  checked={prospectForm.auto_resume}
                  onChange={(e) =>
                    setProspectForm((prev) => ({
                      ...prev,
                      auto_resume: e.target.checked,
                    }))
                  }
                />
                Auto-resume from saved cursors
              </label>
              <label className='inline-flex items-center gap-2'>
                <input
                  type='checkbox'
                  checked={prospectForm.reset_cursors}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      track('Lawyer Outreach Cursors Reset');
                    }
                    setProspectForm((prev) => ({
                      ...prev,
                      reset_cursors: checked,
                    }));
                  }}
                />
                Reset cursors (rescan from page 1)
              </label>
            </div>

            <div className='flex flex-wrap gap-2'>
              <button
                type='button'
                disabled={
                  startProspectMutation.isPending ||
                  Boolean(prospectJobId) ||
                  prospectForm.sources.length === 0
                }
                onClick={() => {
                  setProspectBanner(null);
                  track('Lawyer Outreach Prospect Started', {
                    dry_run: false,
                    sources: prospectForm.sources,
                    pages_per_run: prospectForm.pages_per_run,
                    reset_cursors: prospectForm.reset_cursors,
                  });
                  startProspectMutation.mutate({
                    ...prospectForm,
                    dry_run: false,
                  });
                }}
                className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
              >
                {startProspectMutation.isPending || prospectJobId
                  ? 'Running…'
                  : 'Start prospecting'}
              </button>
              <button
                type='button'
                disabled={
                  startProspectMutation.isPending ||
                  Boolean(prospectJobId) ||
                  prospectForm.sources.length === 0
                }
                onClick={() => {
                  setConfirmDialog({
                    title: 'Dry-run prospecting?',
                    message:
                      'Runs discovery and counts what would be inserted, without writing to the database.',
                    confirmLabel: 'Run dry run',
                    onConfirm: () => {
                      setProspectBanner(null);
                      track('Lawyer Outreach Prospect Started', {
                        dry_run: true,
                        sources: prospectForm.sources,
                        pages_per_run: prospectForm.pages_per_run,
                        reset_cursors: prospectForm.reset_cursors,
                      });
                      startProspectMutation.mutate({
                        ...prospectForm,
                        dry_run: true,
                      });
                    },
                  });
                }}
                className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              >
                Dry run
              </button>
            </div>
          </div>
        </details>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>
              Sender control
            </h2>
            <p className='mt-1 text-sm text-gray-600'>
              Live state of the Celery sender. Toggles are persisted in Redis
              and picked up on the next beat tick — no worker restart needed.
            </p>
          </div>
          <button
            type='button'
            onClick={() => controlQuery.refetch()}
            className='px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50'
          >
            Refresh
          </button>
        </div>

        {controlQuery.isLoading ? (
          <p className='text-sm text-gray-500'>Loading sender state…</p>
        ) : controlQuery.isError ? (
          <p className='text-sm text-red-600'>
            {parseDetail(controlQuery.error)}
          </p>
        ) : control ? (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Status
                </p>
                <p className='mt-1 flex flex-wrap items-center gap-2'>
                  {enabledStatusBadge ? (
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${enabledStatusBadge.className}`}
                    >
                      {enabledStatusBadge.label}
                    </span>
                  ) : null}
                  {sendingLive ? (
                    <span className='text-xs text-green-700 font-semibold'>
                      Will send at next tick
                    </span>
                  ) : (
                    <span className='text-xs text-gray-500'>
                      Next tick will no-op
                    </span>
                  )}
                </p>
                <p className='mt-2 text-xs text-gray-600'>
                  {describeEnabledSource(
                    control.enabled_source,
                    control.enabled
                  )}
                </p>
              </div>
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Next Celery run
                </p>
                <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                  {control.schedule_environment === 'production' &&
                  secondsUntilNextRun !== null
                    ? `in ${formatRelativeSeconds(secondsUntilNextRun)}`
                    : control.schedule_description}
                </p>
                <p className='mt-1 text-xs text-gray-500'>
                  {control.schedule_environment === 'production' &&
                  control.next_run_at_utc
                    ? `${new Date(
                        control.next_run_at_utc
                      ).toLocaleString()} (local) · ${control.schedule_description}`
                    : `Environment: ${control.schedule_environment}`}
                </p>
              </div>
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Approved &amp; ready
                </p>
                <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                  {control.approved_count.toLocaleString()}
                </p>
                <p className='mt-1 text-xs text-gray-500'>
                  Up to {control.daily_limit} sent per task run
                </p>
              </div>
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Redis
                </p>
                <p className='mt-1 text-sm text-gray-900'>
                  {control.redis_configured ? 'Configured' : 'Not configured'}
                </p>
                <p className='mt-1 text-xs text-gray-500'>
                  {control.redis_configured
                    ? 'Toggles below are live.'
                    : 'Set REDIS_URL to enable toggle persistence.'}
                </p>
              </div>
            </div>

            {controlBanner ? (
              <div
                className={`rounded-lg border px-4 py-3 text-sm ${
                  controlBanner.ok
                    ? 'bg-green-50 border-green-200 text-green-900'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                {controlBanner.text}
              </div>
            ) : null}

            <div className='flex flex-wrap gap-2'>
              {control.enabled ? (
                <button
                  type='button'
                  disabled={
                    controlMutation.isPending || !control.redis_configured
                  }
                  onClick={() =>
                    setConfirmDialog({
                      title: 'Disable lawyer outreach sender?',
                      message:
                        'This sets lawyer_outreach:enabled in Redis to clear the override (delete the key). The Celery sender will skip the next beat tick. Approved drafts stay queued and will resume sending when you re-enable.',
                      confirmLabel: 'Disable sender',
                      destructive: true,
                      onConfirm: () =>
                        controlMutation.mutate({ enabled: false }),
                    })
                  }
                  className='px-4 py-2 text-sm font-semibold rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50'
                >
                  Disable sender
                </button>
              ) : (
                <button
                  type='button'
                  disabled={
                    controlMutation.isPending || !control.redis_configured
                  }
                  onClick={() =>
                    setConfirmDialog({
                      title: 'Enable lawyer outreach sender?',
                      message: `This sets lawyer_outreach:enabled=1 in Redis. The next Celery beat tick will pick up approved drafts (currently ${control.approved_count}, capped at ${control.daily_limit} per run). It does not bypass the pause flag — clear that separately if needed.`,
                      confirmLabel: 'Enable sender',
                      onConfirm: () =>
                        controlMutation.mutate({ enabled: true }),
                    })
                  }
                  className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
                >
                  Enable sender
                </button>
              )}
              {control.paused ? (
                <button
                  type='button'
                  disabled={
                    controlMutation.isPending || !control.redis_configured
                  }
                  onClick={() => controlMutation.mutate({ paused: false })}
                  className='px-4 py-2 text-sm font-semibold rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50'
                >
                  Resume (clear pause)
                </button>
              ) : (
                <button
                  type='button'
                  disabled={
                    controlMutation.isPending || !control.redis_configured
                  }
                  onClick={() =>
                    setConfirmDialog({
                      title: 'Pause lawyer outreach sender?',
                      message:
                        'Sets lawyer_outreach:paused=1 in Redis. The next beat tick will no-op until you resume. Use this for a quick hot-pause without disabling the campaign entirely.',
                      confirmLabel: 'Pause sender',
                      onConfirm: () =>
                        controlMutation.mutate({ paused: true }),
                    })
                  }
                  className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                >
                  Pause (hot)
                </button>
              )}
            </div>
          </>
        ) : null}
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
        <details
          className='group'
          onToggle={(event) => {
            if (event.currentTarget.open) {
              markRepliesOpened();
            }
          }}
        >
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900 inline-flex items-center gap-2'>
                Replies
                {hasUnreadReplies ? (
                  <AdminUnreadDot label='New replies since you last opened Replies' />
                ) : null}
                {repliesQuery.data ? (
                  <span className='ml-2 text-sm font-normal text-gray-500'>
                    ({repliesQuery.data.replies.length}
                    {repliesQuery.data.has_more ? '+' : ''})
                  </span>
                ) : null}
              </h2>
              <p className='mt-1 text-sm text-gray-600'>
                Live read-through of Resend Inbound. Click any row to view the
                full message.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-gray-500 group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-gray-500 hidden group-open:inline'>
              − Collapse
            </span>
          </summary>

          <div className='mt-4 space-y-3'>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-xs text-gray-500'>
                {repliesQuery.data?.fetched_at ? (
                  <>
                    Fetched {formatRelative(repliesQuery.data.fetched_at)} from
                    Resend
                  </>
                ) : (
                  'Not fetched yet'
                )}
              </p>
              <button
                type='button'
                onClick={() => repliesQuery.refetch()}
                disabled={repliesQuery.isFetching}
                className='px-2.5 py-1 text-xs font-semibold rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              >
                {repliesQuery.isFetching ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {repliesQuery.isError ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Couldn't load replies: {parseDetail(repliesQuery.error)}
              </div>
            ) : null}
            {repliesQuery.data?.error ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Resend returned an error: {repliesQuery.data.error}
              </div>
            ) : null}

            {repliesQuery.isLoading ? (
              <p className='text-sm text-gray-500'>Loading...</p>
            ) : repliesQuery.data &&
              repliesQuery.data.replies.length === 0 &&
              !repliesQuery.data.error ? (
              <p className='text-sm text-gray-500 italic'>
                No replies yet. Once a lawyer replies to one of your sends,
                Resend will surface it here.
              </p>
            ) : repliesQuery.data ? (
              <ul className='divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden'>
                {repliesQuery.data.replies.map((reply) => {
                  const matched = reply.matched_contact;
                  const youReplied = Boolean(reply.manual_reply_sent_at);
                  return (
                    <li key={reply.id}>
                      <button
                        type='button'
                        onClick={() => {
                          setOpenReplyMode('html');
                          setOpenReplyId(reply.id);
                        }}
                        className={`w-full text-left px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:gap-3 ${
                          youReplied
                            ? 'bg-blue-50/80 hover:bg-blue-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='flex flex-wrap items-baseline gap-2'>
                            <span className='text-sm font-semibold text-gray-900 truncate'>
                              {reply.from_email}
                            </span>
                            {youReplied ? (
                              <span
                                className='inline-flex px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-900 text-[10px] font-semibold uppercase tracking-wide'
                                title={
                                  reply.manual_reply_sent_at
                                    ? `You replied ${formatTimestamp(reply.manual_reply_sent_at) ?? ''}`
                                    : 'You already sent a manual reply'
                                }
                              >
                                You replied
                              </span>
                            ) : null}
                            {matched ? (
                              <span
                                className='inline-flex px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-semibold uppercase tracking-wide'
                                title={`Matched lawyer_outreach_contacts.id=${matched.id}, status=${matched.status}`}
                              >
                                ✓ {matched.lawyer_name}
                              </span>
                            ) : (
                              <span className='inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold uppercase tracking-wide'>
                                no match
                              </span>
                            )}
                            {reply.has_attachments ? (
                              <span className='inline-flex px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide'>
                                📎
                              </span>
                            ) : null}
                          </div>
                          <p className='text-sm text-gray-700 truncate'>
                            {reply.subject || '(no subject)'}
                          </p>
                          <p className='text-[11px] text-gray-400 truncate'>
                            to {reply.to_emails.join(', ') || '—'}
                          </p>
                        </div>
                        <div className='shrink-0 text-right text-[11px] text-gray-500'>
                          {youReplied && reply.manual_reply_sent_at ? (
                            <div
                              className='text-blue-700 font-medium'
                              title={
                                formatTimestamp(reply.manual_reply_sent_at) ??
                                ''
                              }
                            >
                              Replied{' '}
                              {formatRelative(reply.manual_reply_sent_at)}
                            </div>
                          ) : null}
                          <div>{formatRelative(reply.received_at)}</div>
                          {reply.received_at ? (
                            <div
                              className='text-gray-400'
                              title={formatTimestamp(reply.received_at) ?? ''}
                            >
                              {new Date(reply.received_at).toLocaleString()}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </details>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
        <details className='group'>
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>
                Sends
                {sendsQuery.data ? (
                  <span className='ml-2 text-sm font-normal text-gray-500'>
                    ({sendsQuery.data.sends.length}
                    {sendsQuery.data.has_more ? '+' : ''})
                  </span>
                ) : null}
              </h2>
              <p className='mt-1 text-sm text-gray-600'>
                Live read-through of Resend Sent. Click any row to view the
                full message and last engagement event.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-gray-500 group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-gray-500 hidden group-open:inline'>
              − Collapse
            </span>
          </summary>

          <div className='mt-4 space-y-3'>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-xs text-gray-500'>
                {sendsQuery.data?.fetched_at ? (
                  <>
                    Fetched {formatRelative(sendsQuery.data.fetched_at)} from
                    Resend
                  </>
                ) : (
                  'Not fetched yet'
                )}
              </p>
              <button
                type='button'
                onClick={() => sendsQuery.refetch()}
                disabled={sendsQuery.isFetching}
                className='px-2.5 py-1 text-xs font-semibold rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              >
                {sendsQuery.isFetching ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {sendsQuery.isError ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Couldn't load sends: {parseDetail(sendsQuery.error)}
              </div>
            ) : null}
            {sendsQuery.data?.error ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Resend returned an error: {sendsQuery.data.error}
              </div>
            ) : null}

            {sendsQuery.isLoading ? (
              <p className='text-sm text-gray-500'>Loading...</p>
            ) : sendsQuery.data &&
              sendsQuery.data.sends.length === 0 &&
              !sendsQuery.data.error ? (
              <p className='text-sm text-gray-500 italic'>
                No sends yet on this Resend API key.
              </p>
            ) : sendsQuery.data ? (
              <ul className='divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden'>
                {sendsQuery.data.sends.map((send) => {
                  const matched = send.matched_contact;
                  const primaryTo = send.to_emails[0] ?? '';
                  return (
                    <li key={send.id}>
                      <button
                        type='button'
                        onClick={() => {
                          setOpenSendMode('html');
                          setOpenSendId(send.id);
                        }}
                        className='w-full text-left px-3 py-2 hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:gap-3'
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='flex flex-wrap items-baseline gap-2'>
                            <span className='text-sm font-semibold text-gray-900 truncate'>
                              {primaryTo || '(no recipient)'}
                            </span>
                            {matched ? (
                              <span
                                className='inline-flex px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-semibold uppercase tracking-wide'
                                title={`Matched lawyer_outreach_contacts.id=${matched.id}, status=${matched.status}`}
                              >
                                ✓ {matched.lawyer_name}
                              </span>
                            ) : (
                              <span className='inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold uppercase tracking-wide'>
                                no match
                              </span>
                            )}
                            {send.last_event ? (
                              <span
                                className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${sendEventBadgeClass(send.last_event)}`}
                              >
                                {send.last_event}
                              </span>
                            ) : null}
                          </div>
                          <p className='text-sm text-gray-700 truncate'>
                            {send.subject || '(no subject)'}
                          </p>
                          <p className='text-[11px] text-gray-400 truncate'>
                            from {send.from_email || '—'}
                            {send.to_emails.length > 1
                              ? ` · +${send.to_emails.length - 1} more`
                              : ''}
                          </p>
                        </div>
                        <div className='shrink-0 text-right text-[11px] text-gray-500'>
                          <div>{formatRelative(send.sent_at)}</div>
                          {send.sent_at ? (
                            <div
                              className='text-gray-400'
                              title={formatTimestamp(send.sent_at) ?? ''}
                            >
                              {new Date(send.sent_at).toLocaleString()}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </details>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
        <details className='group'>
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>
                Add test contact
              </h2>
              <p className='mt-1 text-sm text-gray-600'>
                Drop one row into{' '}
                <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
                  lawyer_outreach_contacts
                </code>{' '}
                for end-to-end testing with your own email.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-gray-500 group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-gray-500 hidden group-open:inline'>
              − Collapse
            </span>
          </summary>

          <div className='mt-4 space-y-4'>
            <p className='text-sm text-gray-600'>
              Created with{' '}
              <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
                source=manual_test
              </code>{' '}
              and{' '}
              <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
                status=new
              </code>
              . No email row is created — head to{' '}
              <strong>Touch 1 → Needs review</strong> after submitting to draft
              the first touch.
            </p>
            <p className='text-xs text-amber-700'>
              Heads up: the Celery sender skips contacts whose email matches an
              existing Harbor account (status becomes{' '}
              <code className='bg-amber-100 px-1 rounded'>
                skipped_existing_user
              </code>
              ). Use an email that isn&apos;t signed up to actually receive the
              send.
            </p>

            {testContactBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              testContactBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {testContactBanner.text}
          </div>
        ) : null}

        <form
          className='grid grid-cols-1 sm:grid-cols-2 gap-3'
          onSubmit={(e) => {
            e.preventDefault();
            const trimmedName = testContactForm.lawyer_name.trim();
            const trimmedEmail = testContactForm.email.trim();
            if (!trimmedName || !trimmedEmail) {
              setTestContactBanner({
                ok: false,
                text: 'Lawyer name and email are required.',
              });
              return;
            }
            setTestContactBanner(null);
            createTestContactMutation.mutate(testContactForm);
          }}
        >
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Lawyer name *
            </label>
            <input
              value={testContactForm.lawyer_name}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  lawyer_name: e.target.value,
                }))
              }
              placeholder='e.g. Jose (test)'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
              required
            />
          </div>
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Email *
            </label>
            <input
              type='email'
              value={testContactForm.email}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }
              placeholder='you+lawyertest@example.com'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
              required
            />
          </div>
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              State (optional)
            </label>
            <input
              value={testContactForm.state}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  state: e.target.value,
                }))
              }
              placeholder='CA'
              maxLength={64}
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Tier (optional)
            </label>
            <select
              value={testContactForm.tier}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  tier: e.target.value,
                }))
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white'
            >
              <option value=''>(none)</option>
              <option value='1'>1 (highest priority)</option>
              <option value='2'>2</option>
              <option value='3'>3</option>
            </select>
          </div>
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Firm (optional)
            </label>
            <input
              value={testContactForm.firm}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  firm: e.target.value,
                }))
              }
              placeholder='Smith Family Law'
              maxLength={200}
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Practice focus (optional)
            </label>
            <select
              value={testContactForm.practice_focus}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  practice_focus: e.target.value,
                }))
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white'
            >
              <option value=''>(none)</option>
              <option value='enforcement'>enforcement</option>
              <option value='custody'>custody</option>
              <option value='general'>general</option>
            </select>
          </div>
          <div className='sm:col-span-2'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Profile URL (optional)
            </label>
            <input
              value={testContactForm.profile_url}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  profile_url: e.target.value,
                }))
              }
              placeholder='https://example.com/profile/...'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
            <p className='mt-1 text-xs text-gray-500'>
              Used by the LLM draft generator. Leave blank to test the
              no-context path.
            </p>
          </div>
          <div className='sm:col-span-2'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Website (optional)
            </label>
            <input
              value={testContactForm.website}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  website: e.target.value,
                }))
              }
              placeholder='https://example.com'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <div className='sm:col-span-2'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Notes (optional)
            </label>
            <textarea
              value={testContactForm.notes}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              rows={2}
              placeholder='Free text, e.g. "test contact for QA, May 2026"'
              maxLength={2000}
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <div className='sm:col-span-2 flex flex-wrap gap-2'>
            <button
              type='submit'
              disabled={createTestContactMutation.isPending}
              className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
            >
              {createTestContactMutation.isPending
                ? 'Adding...'
                : 'Add test contact'}
            </button>
            <button
              type='button'
              disabled={createTestContactMutation.isPending}
              onClick={() => {
                setTestContactForm(EMPTY_TEST_CONTACT_FORM);
                setTestContactBanner(null);
              }}
              className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
            >
              Reset
            </button>
          </div>
        </form>
          </div>
        </details>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
        <div>
          <h2 className='text-lg font-semibold text-gray-900'>
            Draft review and sent history
          </h2>
          <p className='mt-1 text-sm text-gray-600'>
            Review or write one touch at a time. Saving keeps the row as a
            draft; approving makes it eligible for Celery once lawyer outreach
            sending is enabled. Use Already sent to inspect imported legacy
            emails from the lawyer outreach artifact.
          </p>
        </div>

        <div className='space-y-3'>
          <div>
            <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
              Status
            </p>
            <div className='flex flex-wrap gap-2'>
              {visibleStatusFilters.map((filter) => (
                <button
                  key={filter.value}
                  type='button'
                  onClick={() => {
                    setDraftStatus(filter.value);
                    setSelectedDraft(null);
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                    draftStatus === filter.value
                      ? 'border-primary bg-harbor-50 text-primary'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  title={filter.help}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
              Touch
            </p>
            <div className='flex flex-wrap gap-2'>
              {TOUCH_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type='button'
                  onClick={() => {
                    setDraftEmailKey(filter.value);
                    setSelectedDraft(null);
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                    draftEmailKey === filter.value
                      ? 'border-primary bg-harbor-50 text-primary'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  title={filter.help}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
              Practice focus
            </p>
            <div className='flex flex-wrap gap-2'>
              {PRACTICE_FOCUS_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type='button'
                  onClick={() => {
                    setDraftPracticeFocus(filter.value);
                    setSelectedDraft(null);
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                    draftPracticeFocus === filter.value
                      ? 'border-primary bg-harbor-50 text-primary'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  title={filter.help}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {isFollowUpOutreachTouch(draftEmailKey) &&
          draftStatus !== 'skipped' ? (
            <div className='flex flex-wrap gap-x-6 gap-y-2'>
              <label className='flex items-center gap-2 text-sm text-gray-700 cursor-pointer w-fit'>
                <input
                  type='checkbox'
                  checked={showSkippedLawyers}
                  onChange={(e) => setShowSkippedLawyers(e.target.checked)}
                  className='rounded border-gray-300 text-primary focus:ring-primary'
                />
                Show skipped lawyers
              </label>
              <label className='flex items-center gap-2 text-sm text-gray-700 cursor-pointer w-fit'>
                <input
                  type='checkbox'
                  checked={showSignedUpLawyers}
                  onChange={(e) => setShowSignedUpLawyers(e.target.checked)}
                  className='rounded border-gray-300 text-primary focus:ring-primary'
                />
                Show signed-up lawyers
              </label>
            </div>
          ) : null}
        </div>
        {selectedStatusFilter ||
        selectedTouchFilter ||
        (selectedPracticeFocusFilter &&
          selectedPracticeFocusFilter.value !== 'all') ? (
          <p className='text-xs text-gray-500'>
            {[
              selectedStatusFilter?.help,
              selectedTouchFilter?.help,
              selectedPracticeFocusFilter &&
              selectedPracticeFocusFilter.value !== 'all'
                ? selectedPracticeFocusFilter.help
                : null,
            ]
              .filter(Boolean)
              .join(' ')}
          </p>
        ) : null}

        <div className='flex flex-wrap gap-3 items-end'>
          <div className='flex-1 min-w-[220px]'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Search
            </label>
            <input
              value={draftSearch}
              onChange={(e) => {
                setDraftSearch(e.target.value);
                setSelectedDraft(null);
              }}
              placeholder='Lawyer name, email, state, source'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
        </div>

        {draftBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              draftBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {draftBanner.text}
          </div>
        ) : null}

        <p className='text-sm text-gray-600'>
          {draftsQuery.isLoading ? (
            'Loading list...'
          ) : draftsQuery.isError ? null : (
            <>
              <span className='font-semibold text-gray-900 tabular-nums'>
                {draftListDisplayedCount.toLocaleString()}
              </span>
              {draftListDisplayedCount === 1 ? ' row' : ' rows'} in list
              {draftListHasMore ? (
                <span className='text-gray-500'>
                  {' '}
                  ({draftListTotalCount.toLocaleString()} matching current
                  filters; first {draftListDisplayedCount.toLocaleString()}{' '}
                  loaded)
                </span>
              ) : null}
            </>
          )}
        </p>

        <div className='grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(320px,32rem)] gap-4'>
          <div className='border border-gray-200 rounded-md overflow-hidden w-full max-w-2xl xl:max-w-none'>
            <table className='w-full text-sm table-fixed'>
              <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                <tr>
                  <th
                    scope='col'
                    className='w-8 px-2 py-2.5 text-left'
                    aria-label='Domain health'
                  />
                  <th scope='col' className='w-[38%] px-3 py-2.5 text-left'>
                    Lawyer
                  </th>
                  <th scope='col' className='w-[30%] px-3 py-2.5 text-left'>
                    Email
                  </th>
                  <th scope='col' className='w-[18%] px-3 py-2.5 text-left'>
                    Status
                  </th>
                  <th scope='col' className='w-[8%] px-3 py-2.5 text-left'>
                    Sent
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {draftsQuery.isLoading && (
                  <tr>
                    <td
                      colSpan={5}
                      className='px-4 py-8 text-center text-gray-400'
                    >
                      Loading drafts...
                    </td>
                  </tr>
                )}
                {draftsQuery.isError && (
                  <tr>
                    <td
                      colSpan={5}
                      className='px-4 py-8 text-center text-red-600'
                    >
                      {parseDetail(draftsQuery.error)}
                    </td>
                  </tr>
                )}
                {!draftsQuery.isLoading &&
                  !draftsQuery.isError &&
                  draftListRows.map((row) => {
                    const active = selectedDraft?.contact_id === row.contact_id;
                    const locked =
                      row.sent_at !== null ||
                      row.email_status === 'legacy_sent';
                    return (
                      <tr
                        key={`${row.contact_id}-${row.email_key}`}
                        role='button'
                        tabIndex={0}
                        onClick={() => handleReviewDraft(row)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleReviewDraft(row);
                          }
                        }}
                        className={`cursor-pointer ${
                          active
                            ? 'bg-harbor-50'
                            : 'hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary'
                        }`}
                      >
                        <td className='px-2 py-2 align-top'>
                          <DraftRowDomainIndicator email={row.email} />
                        </td>
                        <td className='px-3 py-2 align-top'>
                          <p className='font-medium text-gray-900 truncate'>
                            {row.lawyer_name}
                          </p>
                          <p className='text-xs text-gray-500 truncate'>
                            {row.state ?? '-'} ·{' '}
                            {row.source ?? 'unknown source'}
                          </p>
                        </td>
                        <td className='px-3 py-2 text-gray-700 align-top truncate'>
                          {row.email ?? (
                            <span className='text-amber-700'>Needs email</span>
                          )}
                        </td>
                        <td className='px-3 py-2 align-top'>
                          <span className='inline-flex max-w-full px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold truncate'>
                            {row.email_status ?? 'none'}
                          </span>
                          {locked ? (
                            <span className='block text-[10px] text-gray-500'>
                              locked
                            </span>
                          ) : null}
                        </td>
                        <td className='px-3 py-2 text-xs text-gray-600 align-top whitespace-nowrap'>
                          {row.sent_at
                            ? new Date(row.sent_at).toLocaleDateString(
                                undefined,
                                { month: 'numeric', day: 'numeric' }
                              )
                            : '-'}
                        </td>
                      </tr>
                    );
                  })}
                {!draftsQuery.isLoading &&
                  !draftsQuery.isError &&
                  draftListRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className='px-4 py-8 text-center text-gray-400'
                      >
                        No matching lawyer outreach drafts.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>

          <div className='border border-gray-200 rounded-md p-4 space-y-3'>
            {selectedDraft ? (
              <>
                <div>
                  <p className='text-sm font-semibold text-gray-900'>
                    {selectedDraft.lawyer_name}
                  </p>
                  <p className='text-xs text-gray-500'>
                    {selectedDraft.email ?? 'No email on contact'} ·{' '}
                    {draftEmailKey}
                  </p>
                </div>
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-2'>
                  <div className='flex flex-wrap gap-2'>
                    {(() => {
                      const websiteUrl = lawyerOutreachWebsiteUrl(selectedDraft);
                      return websiteUrl ? (
                        <a
                          href={websiteUrl}
                          target='_blank'
                          rel='noreferrer'
                          className='text-primary font-semibold hover:underline'
                          title={
                            selectedDraft.website
                              ? undefined
                              : 'Inferred from email domain'
                          }
                        >
                          {selectedDraft.website
                            ? 'Website'
                            : 'Website (domain)'}
                        </a>
                      ) : null;
                    })()}
                    {selectedDraft.profile_url ? (
                      <a
                        href={selectedDraft.profile_url}
                        target='_blank'
                        rel='noreferrer'
                        className='text-primary font-semibold hover:underline'
                      >
                        Directory profile
                      </a>
                    ) : null}
                    {selectedDraft.contact_url ? (
                      <a
                        href={selectedDraft.contact_url}
                        target='_blank'
                        rel='noreferrer'
                        className='text-primary font-semibold hover:underline'
                      >
                        Contact page
                      </a>
                    ) : null}
                  </div>
                  <dl className='grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs'>
                    <div>
                      <dt className='font-semibold text-gray-500'>State</dt>
                      <dd className='text-gray-800'>
                        {selectedDraft.state ?? '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className='font-semibold text-gray-500'>Phone</dt>
                      <dd className='text-gray-800'>
                        {selectedDraft.phone ?? '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className='font-semibold text-gray-500'>Source</dt>
                      <dd className='text-gray-800 break-all'>
                        {selectedDraft.source ?? '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className='font-semibold text-gray-500'>Firm</dt>
                      <dd className='text-gray-800'>
                        {selectedDraft.firm ?? '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className='font-semibold text-gray-500'>
                        Practice focus
                      </dt>
                      <dd className='text-gray-800'>
                        {selectedDraft.practice_focus ?? '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className='font-semibold text-gray-500'>
                        Tier / status
                      </dt>
                      <dd className='text-gray-800'>
                        {selectedDraft.tier ?? '-'} /{' '}
                        {selectedDraft.contact_status}
                      </dd>
                    </div>
                  </dl>
                  {selectedDraft.summary ? (
                    <p className='text-xs text-gray-700 whitespace-pre-wrap'>
                      {selectedDraft.summary}
                    </p>
                  ) : null}
                  {selectedDraft.notes ? (
                    <p className='text-xs text-gray-500 whitespace-pre-wrap'>
                      Notes: {selectedDraft.notes}
                    </p>
                  ) : null}
                </div>
                <CoachEmailDomainHealthCard
                  email={selectedDraft.email}
                  alreadySkipped={selectedDraftAlreadySkipped}
                  skipPending={skipDraftMutation.isPending}
                  skipDisabled={
                    skipDraftMutation.isPending ||
                    selectedDraft.sent_at !== null ||
                    selectedDraft.email_status === 'legacy_sent' ||
                    selectedDraftAlreadySkipped
                  }
                  onRequestSkip={() =>
                    setConfirmDialog({
                      title: 'Skip this touch?',
                      message:
                        'Email domain checks failed or look uncertain. Skip this touch so it leaves your review queue. You can un-skip later by saving as a draft.',
                      confirmLabel: 'Skip touch',
                      onConfirm: () => skipDraftMutation.mutate(),
                    })
                  }
                />
                {selectedDraftSentLabel ? (
                  <div className='rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'>
                    <p className='font-semibold'>{selectedDraftSentLabel}</p>
                    <p className='text-xs'>
                      {selectedDraft.sent_at
                        ? `Sent ${new Date(
                            selectedDraft.sent_at
                          ).toLocaleString()}`
                        : 'Sent timestamp unavailable'}
                    </p>
                  </div>
                ) : null}
                {selectedDraftAlreadySkipped ? (
                  <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
                    <p className='font-semibold'>This touch is skipped</p>
                    <p className='text-xs'>
                      The Celery sender will not pick this up. Save as a draft
                      or approve to revive it.
                    </p>
                  </div>
                ) : null}
                {priorTouches.length > 0 ? (
                  <div className='rounded-lg border border-gray-200 p-3 space-y-3'>
                    <div className='flex items-center justify-between'>
                      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                        Previous touches for this lawyer
                      </p>
                      <p className='text-xs text-gray-400'>
                        {priorTouches.length} of 2 other touches
                      </p>
                    </div>
                    <div className='space-y-3'>
                      {priorTouches.map((prior) => {
                        const sentLabel = formatTimestamp(prior.sent_at);
                        const engagement = summarizePriorEngagement(prior);
                        return (
                          <details
                            key={prior.email_id || prior.email_key}
                            className='border border-gray-200 rounded-md bg-gray-50'
                          >
                            <summary className='cursor-pointer list-none px-3 py-2 flex flex-wrap items-center gap-2'>
                              <span className='text-sm font-semibold text-gray-900'>
                                {TOUCH_LABELS[prior.email_key] ??
                                  prior.email_key}
                              </span>
                              <span className='inline-flex px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold'>
                                {prior.status}
                              </span>
                              {sentLabel ? (
                                <span className='text-xs text-gray-500'>
                                  {sentLabel}
                                </span>
                              ) : null}
                              {prior.last_event ? (
                                <span className='text-xs text-gray-500'>
                                  · last event: {prior.last_event}
                                </span>
                              ) : null}
                              <span className='ml-auto text-xs text-gray-500'>
                                {engagement}
                              </span>
                            </summary>
                            <div className='border-t border-gray-200 px-3 py-2 space-y-2 bg-white rounded-b-md'>
                              <div>
                                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                                  Subject
                                </p>
                                <p className='text-sm text-gray-900 break-words'>
                                  {prior.subject?.trim() || '(no subject)'}
                                </p>
                              </div>
                              <div>
                                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                                  Body
                                </p>
                                <pre className='text-sm text-gray-800 whitespace-pre-wrap font-sans'>
                                  {prior.body?.trim() || '(no body)'}
                                </pre>
                              </div>
                              <dl className='grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-600'>
                                {[
                                  ['Delivered', prior.delivered_at],
                                  ['Opened', prior.opened_at],
                                  ['Clicked', prior.clicked_at],
                                  ['Bounced', prior.bounced_at],
                                  ['Complained', prior.complained_at],
                                ].map(([label, value]) =>
                                  value ? (
                                    <div key={label as string}>
                                      <dt className='font-semibold text-gray-500'>
                                        {label}
                                      </dt>
                                      <dd>{formatTimestamp(value)}</dd>
                                    </div>
                                  ) : null
                                )}
                              </dl>
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1'>
                    Subject
                  </label>
                  <input
                    value={draftSubject}
                    onChange={(e) => setDraftSubject(e.target.value)}
                    readOnly={selectedDraftLocked}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm read-only:bg-gray-50'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1'>
                    Body
                  </label>
                  <textarea
                    value={draftBody}
                    onChange={(e) => setDraftBody(e.target.value)}
                    readOnly={selectedDraftLocked}
                    rows={12}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono read-only:bg-gray-50'
                  />
                  <p className='mt-1 text-xs text-gray-500'>
                    URLs and emails are auto-linked. Use markdown{' '}
                    <code className='bg-gray-100 px-1 rounded'>
                      [label](https://example.com)
                    </code>{' '}
                    when you want custom link text.
                  </p>
                </div>
                <div className='flex flex-wrap gap-2'>
                  <button
                    type='button'
                    disabled={
                      saveDraftMutation.isPending ||
                      selectedDraft.sent_at !== null ||
                      selectedDraft.email_status === 'legacy_sent'
                    }
                    onClick={() =>
                      saveDraftMutation.mutate({
                        status: 'draft',
                        subject: draftSubject,
                        body: draftBody,
                      })
                    }
                    className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                  >
                    Save draft
                  </button>
                  <button
                    type='button'
                    disabled={
                      previewDraftMutation.isPending ||
                      !draftSubject.trim() ||
                      !draftBody.trim()
                    }
                    onClick={() => {
                      setPreviewError(null);
                      previewDraftMutation.mutate({
                        contact_id: selectedDraft.contact_id,
                        subject: draftSubject,
                        body: draftBody,
                      });
                    }}
                    className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                    title='Render the email exactly as the Celery sender will produce it (footer, unsubscribe link, headers).'
                  >
                    {previewDraftMutation.isPending
                      ? 'Rendering...'
                      : 'Preview'}
                  </button>
                  <button
                    type='button'
                    disabled={
                      saveDraftMutation.isPending ||
                      selectedDraft.sent_at !== null ||
                      selectedDraft.email_status === 'legacy_sent' ||
                      !selectedDraft.email
                    }
                    onClick={openApproveConfirmDialog}
                    className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
                  >
                    Approve
                  </button>
                  <button
                    type='button'
                    disabled={
                      skipDraftMutation.isPending ||
                      selectedDraft.sent_at !== null ||
                      selectedDraft.email_status === 'legacy_sent' ||
                      selectedDraftAlreadySkipped
                    }
                    onClick={() =>
                      setConfirmDialog({
                        title: 'Skip this touch?',
                        message:
                          'Marks this touch as intentionally skipped so it stops showing up under "Needs review" / "Due for follow-up". The Celery sender already ignores skipped rows. You can un-skip later by saving as a draft.',
                        confirmLabel: 'Skip touch',
                        onConfirm: () => skipDraftMutation.mutate(),
                      })
                    }
                    className='px-4 py-2 text-sm font-semibold rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50'
                  >
                    {selectedDraftAlreadySkipped
                      ? 'Already skipped'
                      : 'Skip this touch'}
                  </button>
                </div>
                {previewError ? (
                  <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                    Preview failed: {previewError}
                  </div>
                ) : null}
                <div className='rounded-lg border border-gray-200 p-3 space-y-3'>
                  <div className='flex items-baseline justify-between gap-2'>
                    <div>
                      <p className='text-sm font-semibold text-gray-900'>
                        Macros
                      </p>
                      <p className='text-xs text-gray-500'>
                        Reusable subject + body templates. Supports{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{lawyer_name}}'}
                        </code>
                        ,{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{first_name}}'}
                        </code>
                        , and{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{firm}}'}
                        </code>
                        . Seed defaults with{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          scripts/seed_lawyer_outreach_macros.py
                        </code>
                        .
                      </p>
                    </div>
                    {macrosQuery.isFetching ? (
                      <span className='text-xs text-gray-400'>
                        Loading...
                      </span>
                    ) : null}
                  </div>

                  {macroBanner ? (
                    <div
                      className={`rounded-md border px-3 py-2 text-xs ${
                        macroBanner.ok
                          ? 'bg-green-50 border-green-200 text-green-900'
                          : 'bg-red-50 border-red-200 text-red-800'
                      }`}
                    >
                      {macroBanner.text}
                    </div>
                  ) : null}

                  {macrosQuery.data && macrosQuery.data.macros.length > 0 ? (
                    <ul className='space-y-1.5'>
                      {macrosQuery.data.macros.map((macro) => {
                        const scopeLabel =
                          macro.email_key === null
                            ? 'Any touch'
                            : TOUCH_LABELS[macro.email_key] ?? macro.email_key;
                        return (
                          <li
                            key={macro.id}
                            className='flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5'
                          >
                            <div className='flex-1 min-w-0'>
                              <div className='flex flex-wrap items-baseline gap-2'>
                                <span className='text-sm font-semibold text-gray-900 truncate'>
                                  {macro.name}
                                </span>
                                <span className='inline-flex px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700 text-[10px] font-semibold uppercase tracking-wide'>
                                  {scopeLabel}
                                </span>
                              </div>
                              {macro.description ? (
                                <p className='text-xs text-gray-600 mt-0.5'>
                                  {macro.description}
                                </p>
                              ) : null}
                              <p className='text-[11px] text-gray-400 mt-0.5 truncate'>
                                {macro.subject || '(no subject)'}
                              </p>
                            </div>
                            <div className='flex gap-1 shrink-0'>
                              <button
                                type='button'
                                disabled={selectedDraftLocked}
                                onClick={() => handleLoadMacro(macro)}
                                className='px-2 py-1 text-xs font-semibold rounded border border-primary text-primary hover:bg-harbor-50 disabled:opacity-50 disabled:cursor-not-allowed'
                                title={
                                  selectedDraftLocked
                                    ? 'Editor is locked (already sent)'
                                    : 'Load into editor'
                                }
                              >
                                Load
                              </button>
                              <button
                                type='button'
                                disabled={
                                  deleteMacroMutation.isPending &&
                                  deleteMacroMutation.variables === macro.id
                                }
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Delete macro "${macro.name}"? This cannot be undone.`
                                    )
                                  ) {
                                    deleteMacroMutation.mutate(macro.id);
                                  }
                                }}
                                className='px-2 py-1 text-xs font-semibold rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50'
                                title='Delete macro'
                              >
                                ×
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className='text-xs text-gray-500 italic'>
                      No macros saved yet. Save the current draft below to
                      reuse it later.
                    </p>
                  )}

                  <details className='group'>
                    <summary className='cursor-pointer list-none text-xs font-semibold text-primary hover:underline'>
                      <span className='group-open:hidden'>
                        + Save current draft as macro
                      </span>
                      <span className='hidden group-open:inline'>
                        − Hide save form
                      </span>
                    </summary>
                    <form
                      className='mt-2 space-y-2'
                      onSubmit={(e) => {
                        e.preventDefault();
                        const trimmedName = macroSaveName.trim();
                        if (!trimmedName) {
                          setMacroBanner({
                            ok: false,
                            text: 'Macro name is required.',
                          });
                          return;
                        }
                        if (!draftSubject.trim() || !draftBody.trim()) {
                          setMacroBanner({
                            ok: false,
                            text:
                              'Subject and body must both have content before saving as a macro.',
                          });
                          return;
                        }
                        setMacroBanner(null);
                        saveMacroMutation.mutate({
                          name: trimmedName,
                          description:
                            macroSaveDescription.trim() || null,
                          email_key: macroSaveScopeToTouch
                            ? draftEmailKey
                            : null,
                          subject: draftSubject,
                          body: draftBody,
                        });
                      }}
                    >
                      <div>
                        <label className='block text-[11px] font-semibold text-gray-600 mb-1'>
                          Macro name (used as the unique key &mdash; saving
                          with the same name overwrites)
                        </label>
                        <input
                          value={macroSaveName}
                          onChange={(e) => setMacroSaveName(e.target.value)}
                          placeholder='e.g. Touch 2 - soft followup'
                          className='w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm'
                        />
                      </div>
                      <div>
                        <label className='block text-[11px] font-semibold text-gray-600 mb-1'>
                          Description (optional)
                        </label>
                        <input
                          value={macroSaveDescription}
                          onChange={(e) =>
                            setMacroSaveDescription(e.target.value)
                          }
                          placeholder='When to use this macro'
                          className='w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm'
                        />
                      </div>
                      <label className='flex items-start gap-2 text-xs text-gray-700'>
                        <input
                          type='checkbox'
                          checked={macroSaveScopeToTouch}
                          onChange={(e) =>
                            setMacroSaveScopeToTouch(e.target.checked)
                          }
                          className='mt-0.5 h-4 w-4 rounded border-gray-300 text-primary'
                        />
                        <span>
                          Tag as{' '}
                          <strong>
                            {TOUCH_LABELS[draftEmailKey] ?? draftEmailKey}
                          </strong>{' '}
                          (optional label only — macros work on any touch)
                        </span>
                      </label>
                      <button
                        type='submit'
                        disabled={saveMacroMutation.isPending}
                        className='px-3 py-1.5 text-xs font-semibold rounded-md border border-primary text-primary hover:bg-harbor-50 disabled:opacity-50'
                      >
                        {saveMacroMutation.isPending
                          ? 'Saving...'
                          : 'Save macro'}
                      </button>
                    </form>
                  </details>
                </div>
                {!selectedDraftLocked ? (
                  <div className='rounded-lg border border-gray-200 p-3 space-y-2'>
                    <label className='block text-xs font-semibold text-gray-600'>
                      LLM generation notes (optional)
                    </label>
                    <textarea
                      value={generateInstructions}
                      onChange={(e) => setGenerateInstructions(e.target.value)}
                      rows={2}
                      placeholder='e.g. keep it casual, shorter, light on product mentions'
                      className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
                    />
                    <button
                      type='button'
                      disabled={generateDraftMutation.isPending}
                      onClick={() => generateDraftMutation.mutate()}
                      className='px-4 py-2 text-sm font-semibold rounded-md border border-primary text-primary hover:bg-harbor-50 disabled:opacity-50'
                    >
                      {generateDraftMutation.isPending
                        ? 'Generating...'
                        : draftSubject || draftBody
                        ? 'Regenerate with LLM'
                        : 'Generate with LLM'}
                    </button>
                    <p className='text-xs text-gray-500'>
                      Generation only fills the editor. It does not save,
                      approve, or send.
                    </p>
                  </div>
                ) : null}
              </>
            ) : (
              <p className='text-sm text-gray-500'>
                Select a lawyer to review, edit, save, or approve draft copy.
              </p>
            )}
          </div>
        </div>
      </section>

      <ConfirmDialog
        open={confirmDialog !== null}
        state={confirmDialog}
        onClose={() => setConfirmDialog(null)}
      />

      <PreviewDialog
        result={previewResult}
        mode={previewMode}
        onModeChange={setPreviewMode}
        onClose={() => setPreviewResult(null)}
      />

      <ReplyDialog
        open={openReplyId !== null}
        replyId={openReplyId}
        loading={replyDetailQuery.isLoading}
        error={
          replyDetailQuery.isError
            ? parseDetail(replyDetailQuery.error)
            : null
        }
        reply={replyDetailQuery.data ?? null}
        mode={openReplyMode}
        onModeChange={setOpenReplyMode}
        onClose={() => {
          setOpenReplyId(null);
          sendReplyMutation.reset();
        }}
        sendReplyMutation={sendReplyMutation}
      />

      <SendDialog
        open={openSendId !== null}
        loading={sendDetailQuery.isLoading}
        error={
          sendDetailQuery.isError ? parseDetail(sendDetailQuery.error) : null
        }
        send={sendDetailQuery.data ?? null}
        mode={openSendMode}
        onModeChange={setOpenSendMode}
        onClose={() => setOpenSendId(null)}
      />
    </div>
  );
}

function SendDialog({
  open,
  loading,
  error,
  send,
  mode,
  onModeChange,
  onClose,
}: {
  open: boolean;
  loading: boolean;
  error: string | null;
  send: LawyerOutreachSendDetailResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Sent email
              {send?.matched_contact ? (
                <span className='ml-2 text-sm font-normal text-gray-600'>
                  to {send.matched_contact.lawyer_name}
                </span>
              ) : null}
              {send?.last_event ? (
                <span
                  className={`ml-2 inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${sendEventBadgeClass(send.last_event)}`}
                >
                  {send.last_event}
                </span>
              ) : null}
            </h2>
            {send?.sent_at ? (
              <p className='mt-0.5 text-xs text-gray-500'>
                Sent {formatRelative(send.sent_at)} ·{' '}
                {formatTimestamp(send.sent_at)}
              </p>
            ) : null}
          </div>
          <button
            type='button'
            onClick={onClose}
            className='shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100'
          >
            Close
          </button>
        </header>

        {loading ? (
          <div className='p-6 text-sm text-gray-500'>Loading send...</div>
        ) : error ? (
          <div className='m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
            {error}
          </div>
        ) : send ? (
          <>
            <div className='border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1'>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>From</span>
                <span className='truncate'>{send.from_email || '—'}</span>
              </div>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>To</span>
                <span className='truncate'>
                  {send.to_emails.join(', ') || '—'}
                </span>
              </div>
              <div className='flex gap-2 sm:col-span-2'>
                <span className='font-semibold text-gray-500 w-16'>
                  Subject
                </span>
                <span className='truncate'>
                  {send.subject || '(no subject)'}
                </span>
              </div>
              {send.reply_to.length > 0 ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Reply-To
                  </span>
                  <span className='truncate'>{send.reply_to.join(', ')}</span>
                </div>
              ) : null}
              {send.matched_contact ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Matched
                  </span>
                  <span className='truncate'>
                    {send.matched_contact.lawyer_name} ·{' '}
                    <code className='text-[11px] bg-gray-100 px-1 rounded'>
                      contact #{send.matched_contact.id} ·{' '}
                      status={send.matched_contact.status}
                    </code>
                  </span>
                </div>
              ) : null}
            </div>

            <div className='border-b border-gray-200 px-5 pt-3'>
              <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-sm'>
                <button
                  type='button'
                  onClick={() => onModeChange('html')}
                  disabled={!send.html}
                  className={`px-3 py-1.5 ${
                    mode === 'html'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  HTML
                </button>
                <button
                  type='button'
                  onClick={() => onModeChange('text')}
                  disabled={!send.text}
                  className={`px-3 py-1.5 border-l border-gray-300 ${
                    mode === 'text'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  Plain text
                </button>
              </div>
            </div>

            <div className='flex-1 min-h-0 overflow-auto bg-gray-100 p-4'>
              {mode === 'html' && send.html ? (
                <iframe
                  title='Send HTML'
                  srcDoc={send.html}
                  sandbox=''
                  className='w-full h-[60vh] bg-white rounded shadow border border-gray-200'
                />
              ) : send.text ? (
                <pre className='w-full bg-white rounded shadow border border-gray-200 p-4 text-sm font-mono whitespace-pre-wrap text-gray-800'>
                  {send.text}
                </pre>
              ) : (
                <p className='text-sm text-gray-500 italic'>
                  No body returned by Resend for this send.
                </p>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function ReplyDialog({
  open,
  replyId,
  loading,
  error,
  reply,
  mode,
  onModeChange,
  onClose,
  sendReplyMutation,
}: {
  open: boolean;
  replyId: string | null;
  loading: boolean;
  error: string | null;
  reply: LawyerOutreachReplyDetailResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
  sendReplyMutation: UseMutationResult<
    LawyerOutreachReplySendResponse,
    unknown,
    { replyId: string; payload: LawyerOutreachReplySendRequest }
  >;
}) {
  const [composing, setComposing] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [includeQuoted, setIncludeQuoted] = useState(true);
  const [attachFiles, setAttachFiles] = useState<File[]>([]);

  const alreadyReplied = Boolean(reply?.manual_reply_sent_at);
  const sendLocked =
    alreadyReplied ||
    sendReplyMutation.isSuccess ||
    sendReplyMutation.isPending;

  useEffect(() => {
    if (!reply) return;
    setReplySubject(dedupeReplySubject(reply.subject));
    setReplyBody('');
    setIncludeQuoted(true);
    setAttachFiles([]);
    setComposing(false);
  }, [reply?.id]);

  if (!open) return null;

  const canReply = Boolean(reply?.from_email?.trim());
  const noThreading = reply != null && !reply.message_id;

  const handleSend = () => {
    if (!replyId || !reply || sendLocked) return;
    const body = replyBody.trim();
    if (!body) return;
    if (!reply.matched_contact) {
      const ok = window.confirm(
        "Couldn't match this address to a lawyer in your database. " +
          'The reply will still go out but will not be logged against a contact. Continue?'
      );
      if (!ok) return;
    }
    if (noThreading) {
      const ok = window.confirm(
        'This inbound email has no Message-ID, so your reply will start a new thread instead of continuing the existing conversation. Continue?'
      );
      if (!ok) return;
    }
    sendReplyMutation.mutate({
      replyId,
      payload: {
        subject: replySubject.trim() || dedupeReplySubject(reply.subject),
        body,
        include_quoted_original: includeQuoted,
        files: attachFiles,
      },
    });
  };

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Reply
              {reply?.matched_contact ? (
                <span className='ml-2 text-sm font-normal text-gray-600'>
                  from {reply.matched_contact.lawyer_name}
                </span>
              ) : null}
            </h2>
            {reply?.received_at ? (
              <p className='mt-0.5 text-xs text-gray-500'>
                Received {formatRelative(reply.received_at)} ·{' '}
                {formatTimestamp(reply.received_at)}
              </p>
            ) : null}
            {alreadyReplied && reply?.manual_reply_sent_at ? (
              <p className='mt-1 text-xs font-medium text-blue-800'>
                You replied {formatRelative(reply.manual_reply_sent_at)} ·{' '}
                {formatTimestamp(reply.manual_reply_sent_at)}
              </p>
            ) : null}
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            {reply && canReply && !alreadyReplied ? (
              <button
                type='button'
                onClick={() => setComposing((v) => !v)}
                disabled={sendLocked}
                title={
                  noThreading
                    ? 'No Message-ID on this inbound — reply will start a new thread'
                    : undefined
                }
                className='rounded-md border border-primary bg-primary px-3 py-1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'
              >
                {composing
                  ? 'Hide composer'
                  : noThreading
                    ? 'Reply (new thread)'
                    : 'Reply'}
              </button>
            ) : null}
            <button
              type='button'
              onClick={onClose}
              className='rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100'
            >
              Close
            </button>
          </div>
        </header>

        {loading ? (
          <div className='p-6 text-sm text-gray-500'>Loading reply...</div>
        ) : error ? (
          <div className='m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
            {error}
          </div>
        ) : reply ? (
          <>
            {alreadyReplied && reply.manual_reply_sent_at ? (
              <div className='border-b border-blue-200 bg-blue-50 px-5 py-3 text-sm text-blue-900'>
                You already sent a manual reply to this message{' '}
                {formatRelative(reply.manual_reply_sent_at)} (
                {formatTimestamp(reply.manual_reply_sent_at)}).
              </div>
            ) : null}
            <div className='border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1'>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>From</span>
                <span className='truncate'>{reply.from_email}</span>
              </div>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>To</span>
                <span className='truncate'>
                  {reply.to_emails.join(', ') || '—'}
                </span>
              </div>
              <div className='flex gap-2 sm:col-span-2'>
                <span className='font-semibold text-gray-500 w-16'>
                  Subject
                </span>
                <span className='truncate'>
                  {reply.subject || '(no subject)'}
                </span>
              </div>
              {reply.matched_contact ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Matched
                  </span>
                  <span className='truncate'>
                    {reply.matched_contact.lawyer_name} ·{' '}
                    <code className='text-[11px] bg-gray-100 px-1 rounded'>
                      contact #{reply.matched_contact.id} ·{' '}
                      status={reply.matched_contact.status}
                    </code>
                  </span>
                </div>
              ) : null}
              {reply.attachments.length > 0 ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Attach.
                  </span>
                  <span className='truncate'>
                    {reply.attachments
                      .map(
                        (a) =>
                          a.filename ||
                          a.content_type ||
                          a.id ||
                          '(unnamed)'
                      )
                      .join(', ')}
                  </span>
                </div>
              ) : null}
            </div>

            {composing ? (
              <div className='border-b border-gray-200 bg-white px-5 py-4 space-y-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Compose reply
                </p>
                <div className='grid grid-cols-1 gap-2 text-sm'>
                  <label className='flex gap-2 items-baseline'>
                    <span className='font-semibold text-gray-500 w-14 shrink-0'>
                      From
                    </span>
                    <span className='text-gray-700 truncate'>
                      {LAWYER_OUTREACH_FROM_DISPLAY}
                    </span>
                  </label>
                  <label className='flex gap-2 items-baseline'>
                    <span className='font-semibold text-gray-500 w-14 shrink-0'>
                      To
                    </span>
                    <span className='text-gray-700 truncate'>
                      {reply.from_email}
                    </span>
                  </label>
                  <label className='flex flex-col gap-1'>
                    <span className='font-semibold text-gray-500'>Subject</span>
                    <input
                      type='text'
                      value={replySubject}
                      onChange={(e) => setReplySubject(e.target.value)}
                      disabled={sendLocked}
                      className='w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50'
                    />
                  </label>
                  <label className='flex flex-col gap-1'>
                    <span className='font-semibold text-gray-500'>Message</span>
                    <textarea
                      rows={8}
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      disabled={sendLocked}
                      autoFocus
                      placeholder='Write your reply…'
                      className='w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm font-mono disabled:bg-gray-50'
                    />
                  </label>
                  <label className='flex items-center gap-2 text-sm text-gray-700'>
                    <input
                      type='checkbox'
                      checked={includeQuoted}
                      onChange={(e) => setIncludeQuoted(e.target.checked)}
                      disabled={sendLocked}
                    />
                    Include quoted original below
                  </label>
                  <OutreachReplyAttachmentPicker
                    files={attachFiles}
                    onChange={setAttachFiles}
                    disabled={sendLocked}
                  />
                </div>
                {sendReplyMutation.isError ? (
                  <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                    {parseDetail(sendReplyMutation.error)}
                  </div>
                ) : null}
                {sendReplyMutation.isSuccess && sendReplyMutation.data ? (
                  <div className='rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900'>
                    Sent {formatTimestamp(sendReplyMutation.data.sent_at)} — Resend
                    id{' '}
                    <code className='text-xs'>
                      {sendReplyMutation.data.message_id}
                    </code>
                    .
                    {sendReplyMutation.data.contact_status_after
                      ? ' Contact marked replied.'
                      : ' Not logged to a contact.'}
                    {sendReplyMutation.data.attachment_filenames?.length
                      ? ` Attached ${sendReplyMutation.data.attachment_filenames.join(', ')}.`
                      : ''}
                  </div>
                ) : null}
                <div className='flex justify-end'>
                  <button
                    type='button'
                    onClick={handleSend}
                    disabled={
                      sendLocked || replyBody.trim().length === 0
                    }
                    className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'
                  >
                    {sendReplyMutation.isPending ? 'Sending…' : 'Send reply'}
                  </button>
                </div>
              </div>
            ) : null}

            <div className='border-b border-gray-200 px-5 pt-3'>
              <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-sm'>
                <button
                  type='button'
                  onClick={() => onModeChange('html')}
                  disabled={!reply.html}
                  className={`px-3 py-1.5 ${
                    mode === 'html'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  HTML
                </button>
                <button
                  type='button'
                  onClick={() => onModeChange('text')}
                  disabled={!reply.text}
                  className={`px-3 py-1.5 border-l border-gray-300 ${
                    mode === 'text'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  Plain text
                </button>
              </div>
            </div>

            <div className='flex-1 min-h-0 overflow-auto bg-gray-100 p-4'>
              {mode === 'html' && reply.html ? (
                <iframe
                  title='Reply HTML'
                  srcDoc={reply.html}
                  sandbox=''
                  className='w-full h-[min(40vh,360px)] bg-white rounded shadow border border-gray-200'
                />
              ) : reply.text ? (
                <pre className='w-full bg-white rounded shadow border border-gray-200 p-4 text-sm font-mono whitespace-pre-wrap text-gray-800 max-h-[min(40vh,360px)] overflow-auto'>
                  {reply.text}
                </pre>
              ) : (
                <p className='text-sm text-gray-500 italic'>
                  No body returned by Resend for this reply.
                </p>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function PreviewDialog({
  result,
  mode,
  onModeChange,
  onClose,
}: {
  result: LawyerOutreachPreviewResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
}) {
  if (!result) return null;
  const listUnsub = result.headers['List-Unsubscribe'] ?? '';
  const listUnsubPost = result.headers['List-Unsubscribe-Post'] ?? '';
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Preview &mdash; what the recipient will see
            </h2>
            <p className='mt-0.5 text-xs text-gray-500'>
              Rendered through the same path as the Celery sender. Headers
              and footer are byte-identical to a live send.
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100'
            aria-label='Close preview'
          >
            Close
          </button>
        </header>

        <div className='border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1'>
          <div className='flex gap-2'>
            <span className='font-semibold text-gray-500 w-16'>From</span>
            <span className='truncate'>{result.from_email}</span>
          </div>
          <div className='flex gap-2'>
            <span className='font-semibold text-gray-500 w-16'>To</span>
            <span className='truncate'>
              {result.to_email || (
                <em className='text-amber-700'>
                  no email on file (would skip at send time)
                </em>
              )}
            </span>
          </div>
          <div className='flex gap-2 sm:col-span-2'>
            <span className='font-semibold text-gray-500 w-16'>Subject</span>
            <span className='truncate'>{result.subject}</span>
          </div>
          {result.reply_to ? (
            <div className='flex gap-2'>
              <span className='font-semibold text-gray-500 w-16'>
                Reply-To
              </span>
              <span className='truncate'>{result.reply_to}</span>
            </div>
          ) : null}
          <div className='flex gap-2 sm:col-span-2'>
            <span className='font-semibold text-gray-500 w-16'>
              Unsubscribe
            </span>
            <a
              href={result.unsubscribe_url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:underline truncate'
              title={result.unsubscribe_url}
            >
              {result.unsubscribe_url}
            </a>
          </div>
          {listUnsub ? (
            <div className='flex gap-2 sm:col-span-2'>
              <span className='font-semibold text-gray-500 w-16'>Headers</span>
              <span className='truncate text-gray-600'>
                <code className='text-[11px]'>List-Unsubscribe: {listUnsub}</code>
                {listUnsubPost ? (
                  <>
                    <br />
                    <code className='text-[11px]'>
                      List-Unsubscribe-Post: {listUnsubPost}
                    </code>
                  </>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>

        <div className='border-b border-gray-200 px-5 pt-3'>
          <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-sm'>
            <button
              type='button'
              onClick={() => onModeChange('html')}
              className={`px-3 py-1.5 ${
                mode === 'html'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              HTML
            </button>
            <button
              type='button'
              onClick={() => onModeChange('text')}
              className={`px-3 py-1.5 border-l border-gray-300 ${
                mode === 'text'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Plain text
            </button>
          </div>
        </div>

        <div className='flex-1 min-h-0 overflow-auto bg-gray-100 p-4'>
          {mode === 'html' ? (
            <iframe
              title='Email HTML preview'
              srcDoc={result.html}
              sandbox=''
              className='w-full h-[60vh] bg-white rounded shadow border border-gray-200'
            />
          ) : (
            <pre className='w-full bg-white rounded shadow border border-gray-200 p-4 text-sm font-mono whitespace-pre-wrap text-gray-800'>
              {result.text}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/lawyer-outreach')({
  component: LawyerOutreachAdminPage,
});
